// Mentions tab — paginated feed + chart + filters + right-rail summaries.
import './brand-mentions.scss';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    bp_mentions, bp_chart, bp_sentiment,
    bp_influencers, bp_sources, bp_filters,
    bp_undiscovered_sources, bp_assistant_ask,
    bp_visit_mention, bp_unvisit_mention, bp_mute_domain, bp_set_mention_verdict,
    bp_translate_mention,
    bp_list_mention_tags, bp_apply_mention_tag, bp_unapply_mention_tag,
    bp_delete_mention_tag,
    bp_list_report_queue, bp_clear_report_queue,
    bp_queue_mention, bp_unqueue_mention,
    bp_request_mention_takedown,
    bp_rescan_mentions, bp_scan_status,
    bp_mentions_since, bp_anchor_preview,
    bp_alert_latency,
    bp_mention_comments,
} from '../../../services';
import CommonPagination from '../../../Components/CommonPagination';
import ReactMarkdown from 'react-markdown';
import {
    SENTIMENT_COLOR, formatNumber, timeAgo, unwrap, formatLocalDateTime,
    Skeleton, EmptyState, highlightHashtags, mdNormalize
} from './_shared';
import { AccountAvatar, PlatformChip, profileUrlFor } from './_avatarKit';
import FullPageLoader from '../../../Components/FullPageLoader';

// Brand24-parity: flag the days where mention volume is anomalously high
// so the user's eye lands on the spike before they scroll. Threshold is
// mean + 1.5σ over the full visible window, with a floor of 2× the mean
// so a series of all-zeros plus one (2) doesn't false-positive every day.
function spikeStats(series) {
    if (!series || series.length < 4) return { spikes: new Set(), mean: 0, stddev: 0, threshold: 0 };
    const values = series.map(d => d.mentions || 0);
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    if (mean === 0) return { spikes: new Set(), mean: 0, stddev: 0, threshold: 0 };
    const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length;
    const stddev = Math.sqrt(variance);
    const threshold = Math.max(mean + 1.5 * stddev, mean * 2);
    const spikes = new Set(
        series.map((d, i) => ((d.mentions || 0) > threshold && (d.mentions || 0) > 2) ? i : null)
            .filter(i => i !== null)
    );
    return { spikes, mean, stddev, threshold };
}

// Format reach / view counts the way industry-standard analytics dashboards
// do: 1.2k / 3.4M / 1.1B. Keeps tooltip values legible on small charts.
function fmtCompact(n) {
    if (n == null || isNaN(n)) return '—';
    const abs = Math.abs(n);
    if (abs >= 1e9) return (n / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + 'B';
    if (abs >= 1e6) return (n / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
    if (abs >= 1e3) return (n / 1e3).toFixed(abs >= 1e4 ? 0 : 1) + 'k';
    return Math.round(n).toString();
}

function MentionsReachChart({ series, mode, onSpikeClick }) {
    const w = 700, h = 200, pad = 30;
    const svgRef = useRef(null);
    const [hoverIdx, setHoverIdx] = useState(null);

    if (!series || series.length === 0) {
        return <div className="bm_chart_empty">No mentions in this date range.</div>;
    }
    let yMax;
    if (mode === 'sentiment') {
        yMax = Math.max(1, ...series.map(d =>
            (d.positive || 0) + (d.neutral || 0) + (d.negative || 0)));
    } else {
        yMax = Math.max(1, ...series.map(d => Math.max(d.mentions || 0, d.reach || 0)));
    }
    // With a single bucket we center the point so it doesn't pin to the
    // left padding (which made the chart look empty when filters narrowed
    // the window down to one day).
    const x = (i) => series.length === 1
        ? w / 2
        : pad + (i * (w - 2 * pad)) / (series.length - 1);
    const y = (v) => h - pad - ((v / yMax) * (h - 2 * pad));
    const { spikes, mean, stddev } = mode === 'mentions_reach'
        ? spikeStats(series)
        : { spikes: new Set(), mean: 0, stddev: 0 };

    // Map a mouse event's clientX to the nearest series index. Uses the SVG
    // viewBox so it stays accurate when the chart scales responsively.
    const onMove = (e) => {
        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const xPx = (e.clientX - rect.left) * (w / rect.width);
        // Convert pixel x → series fractional index via the same affine
        // transform `x()` uses, inverted.
        const denom = Math.max(1, series.length - 1);
        const frac = ((xPx - pad) / Math.max(1, (w - 2 * pad))) * denom;
        const idx = Math.max(0, Math.min(series.length - 1, Math.round(frac)));
        setHoverIdx(idx);
    };
    const onLeave = () => setHoverIdx(null);

    const renderHoverGuide = () => {
        if (hoverIdx == null) return null;
        const cx = x(hoverIdx);
        return (
            <g pointerEvents="none">
                <line x1={cx} y1={pad - 6} x2={cx} y2={h - pad}
                    stroke="#94a3b8" strokeWidth="1" strokeDasharray="3,3"
                    opacity="0.7" />
            </g>
        );
    };

    const hovered = hoverIdx != null ? series[hoverIdx] : null;
    const tooltipLeftPct = hoverIdx != null ? (x(hoverIdx) / w) * 100 : 0;
    // Flip tooltip to the left of the cursor when we're past the midpoint
    // so it never gets clipped at the right edge.
    const flipTooltip = tooltipLeftPct > 65;

    if (mode === 'sentiment') {
        const barW = (w - 2 * pad) / Math.max(series.length, 1) * 0.7;
        return (
            <div className="bm_chart_wrap">
                <svg width={w} height={h} className="bm_chart"
                    viewBox={`0 0 ${w} ${h}`}
                    ref={svgRef}
                    onMouseMove={onMove} onMouseLeave={onLeave}>
                    {series.map((d, i) => {
                        const xc = x(i) - barW / 2;
                        const hP = ((d.positive || 0) / yMax) * (h - 2 * pad);
                        const hN = ((d.neutral || 0) / yMax) * (h - 2 * pad);
                        const hNeg = ((d.negative || 0) / yMax) * (h - 2 * pad);
                        return (
                            <g key={d.bucket} opacity={hoverIdx != null && hoverIdx !== i ? 0.55 : 1}>
                                <rect x={xc} y={h - pad - hP} width={barW} height={hP} fill={SENTIMENT_COLOR.positive} />
                                <rect x={xc} y={h - pad - hP - hN} width={barW} height={hN} fill={SENTIMENT_COLOR.neutral} />
                                <rect x={xc} y={h - pad - hP - hN - hNeg} width={barW} height={hNeg} fill={SENTIMENT_COLOR.negative} />
                            </g>
                        );
                    })}
                    {renderHoverGuide()}
                    {series.map((d, i) => (
                        (i % Math.max(1, Math.floor(series.length / 6))) === 0 ? (
                            <text key={d.bucket + '-lbl'} x={x(i)} y={h - 5}
                                fontSize="10" textAnchor="middle"
                                fill="#94a3b8">{d.bucket}</text>
                        ) : null
                    ))}
                </svg>
                {hovered && (
                    <div className={`bm_chart_tooltip ${flipTooltip ? 'flip' : ''}`}
                        style={{ left: `${tooltipLeftPct}%` }}>
                        <div className="bm_chart_tooltip_date">{hovered.bucket}</div>
                        <div className="bm_chart_tooltip_row">
                            <span className="bm_chart_tt_dot" style={{ background: SENTIMENT_COLOR.positive }} />
                            <span className="bm_chart_tt_label">Positive</span>
                            <span className="bm_chart_tt_val">{fmtCompact(hovered.positive || 0)}</span>
                        </div>
                        <div className="bm_chart_tooltip_row">
                            <span className="bm_chart_tt_dot" style={{ background: SENTIMENT_COLOR.neutral }} />
                            <span className="bm_chart_tt_label">Neutral</span>
                            <span className="bm_chart_tt_val">{fmtCompact(hovered.neutral || 0)}</span>
                        </div>
                        <div className="bm_chart_tooltip_row">
                            <span className="bm_chart_tt_dot" style={{ background: SENTIMENT_COLOR.negative }} />
                            <span className="bm_chart_tt_label">Negative</span>
                            <span className="bm_chart_tt_val">{fmtCompact(hovered.negative || 0)}</span>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const mentionsPath = series.map((d, i) =>
        `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(d.mentions || 0)}`).join(' ');
    const reachPath = series.map((d, i) =>
        `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(d.reach || 0)}`).join(' ');

    // Hover dots + delta vs prior bucket for the tooltip.
    const hoverDots = hovered ? [
        { cx: x(hoverIdx), cy: y(hovered.mentions || 0), fill: '#3b82f6' },
        { cx: x(hoverIdx), cy: y(hovered.reach || 0), fill: '#16a34a' },
    ] : [];
    const prior = (hoverIdx != null && hoverIdx > 0) ? series[hoverIdx - 1] : null;
    const delta = (cur, base) => {
        if (base == null || base === 0) return null;
        return Math.round(((cur - base) / base) * 100);
    };
    const isSpike = hoverIdx != null && spikes.has(hoverIdx);
    const sigma = hovered && stddev > 0 ? ((hovered.mentions || 0) - mean) / stddev : 0;

    return (
        <div className="bm_chart_wrap">
            <svg width={w} height={h} className="bm_chart"
                viewBox={`0 0 ${w} ${h}`}
                ref={svgRef}
                onMouseMove={onMove} onMouseLeave={onLeave}>
                <path d={mentionsPath} stroke="#3b82f6" strokeWidth="2" fill="none" />
                <path d={reachPath} stroke="#16a34a" strokeWidth="2" fill="none" />
                {/* A single-bucket series collapses to "M x y" with no L,
                    so the path is invisible. Always render explicit point
                    markers in that case so the user sees the value. */}
                {series.length === 1 && (
                    <g pointerEvents="none">
                        <circle cx={x(0)} cy={y(series[0].mentions || 0)} r="5"
                            fill="#3b82f6" stroke="#fff" strokeWidth="2" />
                        <circle cx={x(0)} cy={y(series[0].reach || 0)} r="5"
                            fill="#16a34a" stroke="#fff" strokeWidth="2" />
                    </g>
                )}
                {renderHoverGuide()}
                {hoverDots.map((d, i) => (
                    <g key={'hd' + i} pointerEvents="none">
                        <circle cx={d.cx} cy={d.cy} r="5"
                            fill="#fff" stroke={d.fill} strokeWidth="2" />
                    </g>
                ))}
                {series.map((d, i) => (
                    (i % Math.max(1, Math.floor(series.length / 6))) === 0 ? (
                        <text key={d.bucket + '-lbl'} x={x(i)} y={h - 5}
                            fontSize="10" textAnchor="middle"
                            fill="#94a3b8">{d.bucket}</text>
                    ) : null
                ))}
                {/* Spike markers — click opens a popover with the bucket date,
                    count, reach, why-it's-a-spike maths, and a CTA that
                    filters the mention feed to just that day. */}
                {[...spikes].map(i => {
                    const d = series[i];
                    const cx = x(i), cy = y(d.mentions || 0);
                    const sig = stddev > 0 ? ((d.mentions || 0) - mean) / stddev : 0;
                    return (
                        <g key={'spike-' + i} className="bm_spike_marker"
                            style={{ cursor: 'pointer' }}
                            onClick={() => onSpikeClick && onSpikeClick({
                                index: i, bucket: d.bucket,
                                mentions: d.mentions || 0, reach: d.reach || 0,
                                mean, stddev, sigma: sig,
                                cxPct: (cx / w) * 100, cyPx: cy,
                            })}>
                            <circle cx={cx} cy={cy - 28} r="16" fill="transparent" />
                            <line x1={cx} y1={cy} x2={cx} y2={cy - 22}
                                stroke="#d97706" strokeWidth="1" strokeDasharray="2,2" />
                            <circle cx={cx} cy={cy - 28} r="9"
                                fill="#d97706" stroke="#fff" strokeWidth="1.5" />
                            <text x={cx} y={cy - 24} fontSize="12" fontWeight="700"
                                textAnchor="middle" fill="#fff"
                                style={{ pointerEvents: 'none' }}>!</text>
                        </g>
                    );
                })}
            </svg>
            {hovered && (
                <div className={`bm_chart_tooltip ${flipTooltip ? 'flip' : ''}`}
                    style={{ left: `${tooltipLeftPct}%` }}>
                    <div className="bm_chart_tooltip_date">
                        {hovered.bucket}
                        {isSpike && <span className="bm_chart_tt_spike">SPIKE · +{sigma.toFixed(1)}σ</span>}
                    </div>
                    <div className="bm_chart_tooltip_row">
                        <span className="bm_chart_tt_dot" style={{ background: '#3b82f6' }} />
                        <span className="bm_chart_tt_label">Mentions</span>
                        <span className="bm_chart_tt_val">{fmtCompact(hovered.mentions || 0)}</span>
                        {prior && delta(hovered.mentions, prior.mentions) != null && (
                            <span className={`bm_chart_tt_delta ${delta(hovered.mentions, prior.mentions) >= 0 ? 'up' : 'down'}`}>
                                {delta(hovered.mentions, prior.mentions) >= 0 ? '▲' : '▼'}
                                {Math.abs(delta(hovered.mentions, prior.mentions))}%
                            </span>
                        )}
                    </div>
                    <div className="bm_chart_tooltip_row">
                        <span className="bm_chart_tt_dot" style={{ background: '#16a34a' }} />
                        <span className="bm_chart_tt_label">Reach</span>
                        <span className="bm_chart_tt_val">{fmtCompact(hovered.reach || 0)}</span>
                        {prior && delta(hovered.reach, prior.reach) != null && (
                            <span className={`bm_chart_tt_delta ${delta(hovered.reach, prior.reach) >= 0 ? 'up' : 'down'}`}>
                                {delta(hovered.reach, prior.reach) >= 0 ? '▲' : '▼'}
                                {Math.abs(delta(hovered.reach, prior.reach))}%
                            </span>
                        )}
                    </div>
                    {isSpike && (
                        <div className="bm_chart_tooltip_hint">
                            Click <strong>!</strong> for spike detail + posts from this day
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// CEPI RFP row 6 — misinfo SLA: every misinformation-tagged mention must be
// detected within 24h of being posted. Narrative tags here mirror the
// scanner's misinfo taxonomy (see scanner/utils/brand_mention_scan.py's
// NARRATIVE_TAGS); kept as a Set so the membership check stays O(1).
const MISINFO_NARRATIVES = new Set([
    'misinformation', 'disinformation', 'vaccine_misinformation',
    'vaccine_safety_misinfo', 'safety_concern', 'conspiracy',
    'coordinated_campaign',
]);
const MISINFO_SLA_HOURS = 24;

function misinfoSlaStatus(m) {
    if (!m.posted_at) return null;
    if (!MISINFO_NARRATIVES.has(m.narrative_tag)) return null;
    const ageHours = (Date.now() - new Date(m.posted_at).getTime()) / 36e5;
    return {
        ageHours,
        met: ageHours <= MISINFO_SLA_HOURS,
        deadline: MISINFO_SLA_HOURS,
    };
}

// CEPI RFP #28 — Alert-latency SLA pill.
// Renders p95 detection-to-alert time over the last 30 days plus the % of
// alerts that landed inside the 24h SLA. Tooltip shows p50 / p90 / p99 +
// per-platform breakdown so a customer can see where latency concentrates.
function _fmtLatency(seconds) {
    if (seconds == null) return '—';
    const s = Number(seconds);
    if (!isFinite(s) || s < 0) return '—';
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
    return `${(s / 86400).toFixed(1)}d`;
}

function AlertLatencyPill({ data }) {
    if (!data || !data.measured || data.measured <= 0) return null;
    const p = data.percentiles_seconds || {};
    const slaPct = data.sla_compliance_pct;
    const ok = slaPct == null ? true : slaPct >= 95;
    const palette = ok
        ? { bg: 'rgba(16,185,129,0.12)', fg: '#047857', border: 'rgba(16,185,129,0.45)', dot: '#10b981' }
        : { bg: 'rgba(245,158,11,0.15)', fg: '#b45309', border: 'rgba(245,158,11,0.55)', dot: '#f59e0b' };
    const byPlat = (data.by_platform || []).slice(0, 5)
        .map(b => `  ${b.platform}: p50 ${_fmtLatency(b.p50)} · p95 ${_fmtLatency(b.p95)} (${b.count})`)
        .join('\n');
    const title = (
        `Alert latency over last ${data.window_days || 30} days — time between ` +
        `post going live and our scanner persisting it.\n` +
        `Measured on ${data.measured} of ${data.total} mentions.\n` +
        `p50 ${_fmtLatency(p.p50)} · p90 ${_fmtLatency(p.p90)} · ` +
        `p95 ${_fmtLatency(p.p95)} · p99 ${_fmtLatency(p.p99)} · ` +
        `max ${_fmtLatency(p.max)}.\n` +
        `SLA: ${slaPct == null ? '—' : slaPct + '%'} inside 24h.` +
        (byPlat ? `\n\nBy platform:\n${byPlat}` : '')
    );
    return (
        <span title={title} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '2px 9px', borderRadius: 999,
            background: palette.bg, color: palette.fg,
            border: `1px solid ${palette.border}`,
            fontSize: 11, fontWeight: 600, letterSpacing: 0.2,
            cursor: 'help',
        }}>
            <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: palette.dot,
            }} />
            Alert p95 {_fmtLatency(p.p95)}
            {slaPct != null && (
                <span style={{ opacity: 0.85 }}>· SLA {slaPct}%</span>
            )}
        </span>
    );
}

// Build the windowed page list shown in the paginator. We always show
// first + last + a window of ±2 around the current page, with ellipsis
// gaps. Returns an array like [1, '…', 4, 5, 6, '…', 24].
function paginationWindow(current, total) {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    const out = [1];
    const start = Math.max(2, current - 2);
    const end = Math.min(total - 1, current + 2);
    if (start > 2) out.push('…');
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push('…');
    out.push(total);
    return out;
}

function Pagination({ page, pages, total, pageSize, onPage, onPageSize, position }) {
    if (!total) return null;
    const startIdx = (page - 1) * pageSize + 1;
    const endIdx = Math.min(page * pageSize, total);
    const windowed = paginationWindow(page, pages);
    return (
        <div className={`bm_pagination bm_pagination--${position}`}>
            <div className="bm_pagination_label">
                Showing <strong>{startIdx.toLocaleString()}</strong>–<strong>{endIdx.toLocaleString()}</strong>{' '}
                of <strong>{total.toLocaleString()}</strong> mentions
            </div>
            {pages > 1 && (
                <div className="bm_pagination_nav" role="navigation" aria-label="Mentions pagination">
                    <button onClick={() => onPage(page - 1)}
                        disabled={page <= 1}
                        aria-label="Previous page">‹</button>
                    {windowed.map((p, i) => p === '…' ? (
                        <span key={'gap' + i} className="bm_pagination_gap" aria-hidden="true">…</span>
                    ) : (
                        <button key={p}
                            className={p === page ? 'active' : ''}
                            aria-current={p === page ? 'page' : undefined}
                            onClick={() => onPage(p)}>
                            {p}
                        </button>
                    ))}
                    <button onClick={() => onPage(page + 1)}
                        disabled={page >= pages}
                        aria-label="Next page">›</button>
                </div>
            )}
            {onPageSize && (
                <div className="bm_pagination_size">
                    <label>
                        Per page
                        <select value={pageSize}
                            onChange={e => onPageSize(Number(e.target.value))}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </label>
                </div>
            )}
        </div>
    );
}

function hostFromUrl(url) {
    try { return new URL(url).hostname.replace(/^www\./, ''); }
    catch (_) { return ''; }
}

// Human-readable label for the org's scheduled-scan cadence. Mirrors the
// labels in SettingsTab's tier dropdown so the rate-limit banner agrees
// with whatever the admin picked there.
function cadenceLabel(minutes) {
    if (minutes == null) return 'on schedule';
    if (minutes === 0) return 'are turned off';
    if (minutes < 60) return `every ${minutes} min`;
    if (minutes === 60) return 'every hour';
    if (minutes % 60 === 0) return `every ${minutes / 60} hours`;
    return `every ${minutes} min`;
}

// The backend's rate_limited message hardcodes "every 2h" regardless of
// the org's actual cadence. Preserve the "Try again in ~N min" portion
// (server-computed cooldown) but rewrite the tail using the cadence the
// admin selected in Settings.
function formatRateLimitedMessage(backendMessage, cadenceMinutes) {
    const cad = cadenceLabel(cadenceMinutes);
    const tail = cadenceMinutes === 0
        ? 'Scheduled scans are turned off — only manual scans run.'
        : `Scheduled scans still run ${cad}.`;
    if (!backendMessage) return `Manual scan rate-limited. ${tail}`;
    const tryAgain = backendMessage.match(/Try again[^.]*\./i);
    return tryAgain
        ? `Manual scan rate-limited. ${tryAgain[0]} ${tail}`
        : `Manual scan rate-limited. ${tail}`;
}

// Spike-detail popover. Renders below the chart card when the analyst
// clicks a `!` marker. Loads the top-reach mentions for that bucket via
// the new from_date/to_date filter and exposes a "Filter feed to this day"
// CTA so the analyst can drill into the same window in the main feed.
function SpikePanel({ spike, onClose, onDrillIn, onShowAll }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!spike) return;
        let alive = true;
        setLoading(true);
        bp_mentions({
            from_date: spike.bucket,
            to_date: spike.bucket,
            sort: '-reach_score',
            page_size: 5,
            days: 365,
        }).then(res => {
            if (!alive) return;
            const body = unwrap(res) || {};
            setItems(body.results || []);
        }).catch(() => alive && setItems([]))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [spike]);

    if (!spike) return null;

    const sigmaLabel = isFinite(spike.sigma) && spike.sigma > 0
        ? `+${spike.sigma.toFixed(1)}σ above mean`
        : 'anomalous day';
    const xPctRatio = (spike.mentions / Math.max(spike.mean || 1, 0.1));

    return (
        <div className="bm_spike_panel"
            role="dialog" aria-label={`Spike details for ${spike.bucket}`}>
            <div className="bm_spike_panel_head">
                <div>
                    <div className="bm_spike_panel_label">Spike on {spike.bucket}</div>
                    <div className="bm_spike_panel_stats">
                        <span><strong>{spike.mentions}</strong> mentions</span>
                        <span><strong>{Math.round(spike.reach)}</strong> reach</span>
                        <span className="bm_spike_sigma">{sigmaLabel}</span>
                        <span>(window avg {spike.mean.toFixed(1)}, this day is {xPctRatio.toFixed(1)}×)</span>
                    </div>
                </div>
                <button onClick={onClose} aria-label="Close spike detail"
                    className="bm_spike_panel_close">×</button>
            </div>

            <div className="bm_spike_panel_body">
                {loading && <div style={{ color: '#94a3b8', fontSize: 12 }}>Loading top posts for this day…</div>}
                {!loading && items.length === 0 && (
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>
                        No mentions captured exactly on this day (the chart
                        bucket may span a wider window in weeks / months mode).
                        Use “Filter feed to this day” for the full slice.
                    </div>
                )}
                {!loading && items.length > 0 && (
                    <ul className="bm_spike_panel_items">
                        {items.map(m => (
                            <li key={m.id}>
                                <span className="bm_spike_item_meta">
                                    <strong>{m.handle || '(no handle)'}</strong> · {m.platform}
                                    {m.reach_score ? <em> · reach {Math.round(m.reach_score)}</em> : null}
                                </span>
                                <span className="bm_spike_item_text">
                                    {(m.text || '').slice(0, 200)}{(m.text || '').length > 200 ? '…' : ''}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="bm_spike_panel_foot">
                <button className="btn btn-sm btn-primary"
                    onClick={() => { onDrillIn(spike); onClose(); }}>
                    Filter feed to {spike.bucket}
                </button>
                <button className="btn btn-sm btn-secondary"
                    onClick={() => { onShowAll(); onClose(); }}>
                    Show all
                </button>
            </div>
        </div>
    );
}

function TakedownModal({ mention, onClose, onSubmitted }) {
    const [reason, setReason] = useState('');
    const [targetKind, setTargetKind] = useState('post');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        setBusy(true); setError('');
        try {
            const res = await bp_request_mention_takedown(mention.id, { reason, target_kind: targetKind });
            const data = res?.statusCode?.response_data?.data ?? res?.statusCode ?? {};
            onSubmitted && onSubmitted(mention.id, {
                id: data.takedown_request_id,
                status: data.status_value || 'requested',
                target_kind: data.target_kind,
                sla_initiate_target: data.sla_initiate_target,
                sla_resolve_target: data.sla_resolve_target,
                created_date: new Date().toISOString(),
                already_existed: data.created === false,
            });
            onClose();
        } catch (e) {
            setError('Could not submit takedown — try again.');
        } finally { setBusy(false); }
    };

    return (
        <div className="bm_modal_overlay" onClick={onClose}>
            <div className="bm_modal" onClick={e => e.stopPropagation()}
                role="dialog" aria-label="Request takedown">
                <div className="bm_modal_head">
                    <h4>Request takedown</h4>
                    <button onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="bm_modal_body">
                    <p style={{ fontSize: 12, color: '#475569', margin: '0 0 12px' }}>
                        Submit a takedown request through NMT’s abuse-channel
                        dispatcher (X / Reddit / Cloudflare / host / registrar).
                        SLA timers start the moment you click Submit:
                        <strong> initiate ≤ 24h, resolve ≤ 5 days.</strong>
                    </p>
                    <div className="bm_modal_target">
                        <div className="bm_modal_target_post"><strong>{mention.handle}</strong> · {mention.platform}</div>
                        <div className="bm_modal_target_text">{(mention.text || '').slice(0, 280)}{(mention.text || '').length > 280 ? '…' : ''}</div>
                        <a href={mention.url} target="_blank" rel="noopener noreferrer"
                            className="bm_modal_target_url">{mention.url}</a>
                    </div>

                    <div className="bm_modal_kind">
                        <label className={targetKind === 'post' ? 'on' : ''}>
                            <input type="radio" name="kind" value="post"
                                checked={targetKind === 'post'}
                                onChange={() => setTargetKind('post')} />
                            <div>
                                <strong>This post only</strong>
                                <small>Removes the specific URL via the platform’s abuse channel. Right choice for X / Reddit / Bluesky posts.</small>
                            </div>
                        </label>
                        <label className={targetKind === 'domain' ? 'on' : ''}>
                            <input type="radio" name="kind" value="domain"
                                checked={targetKind === 'domain'}
                                onChange={() => setTargetKind('domain')} />
                            <div>
                                <strong>Whole domain</strong>
                                <small>Reports the entire host. Right choice for phishing / typosquat sites.</small>
                            </div>
                        </label>
                    </div>

                    <label className="bm_modal_reason">
                        <span>Reason (optional)</span>
                        <textarea rows={3} value={reason}
                            placeholder="Defamatory / impersonation / phishing / etc."
                            onChange={e => setReason(e.target.value)} />
                    </label>

                    {error && <div className="bm_modal_error">{error}</div>}
                </div>
                <div className="bm_modal_foot">
                    <button className="btn btn-sm btn-secondary"
                        onClick={onClose} disabled={busy}>Cancel</button>
                    <button className="btn btn-sm btn-primary"
                        onClick={submit} disabled={busy}>
                        {busy ? 'Submitting…' : 'Submit takedown request'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Read comments from a few common API response shapes so a backend rename
// doesn't break the UI.
function extractComments(body) {
    const data = body?.response_data?.data ?? body?.data ?? body ?? {};
    const list = data.comments || data.results || data.items || [];
    const total = data.total ?? data.total_count ?? data.count ?? list.length;
    return { list: Array.isArray(list) ? list : [], total };
}

function CommentsModal({ mention, onClose }) {
    const PAGE_SIZE = 10;
    const [page, setPage] = useState(0);              // 0-indexed for ReactPaginate
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [allComments, setAllComments] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setError('');
            try {
                const res = await bp_mention_comments(mention.id, { refresh: 1 });
                if (cancelled) return;
                const body = res?.statusCode ?? res?.data ?? res;
                const { list } = extractComments(body);
                setAllComments(list);
                setPage(0);
            } catch (e) {
                if (!cancelled) setError('Could not load comments — try again.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [mention.id]);

    const total = allComments.length;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const comments = allComments.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const bodyRef = useRef(null);
    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }, [page]);

    return (
        <div className="bm_modal_overlay" onClick={onClose}>
            <div className="bm_modal bm_modal--comments" onClick={e => e.stopPropagation()}
                role="dialog" aria-label="Comments">
                <div className="bm_modal_head">
                    <h4>Comments {total > 0 && <span style={{ color: '#64748b', fontWeight: 500 }}>· {total}</span>}</h4>
                    <button onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="bm_modal_body" ref={bodyRef}>
                    {/* <div className="bm_modal_target">
                        <div className="bm_modal_target_post">
                            <strong>{mention.handle}</strong> · {mention.platform}
                        </div>
                        <div className="bm_modal_target_text">
                            {(mention.text || '').slice(0, 280)}{(mention.text || '').length > 280 ? '…' : ''}
                        </div>
                    </div> */}

                    {loading && <div className="bm_comments_state">Loading comments…</div>}
                    {error && !loading && <div className="bm_modal_error">{error}</div>}
                    {!loading && !error && comments.length === 0 && (
                        <div className="bm_comments_state">No comments yet.</div>
                    )}

                    {!loading && !error && comments.length > 0 && (
                        <ul className="bm_comments_list">
                            {comments.map((c, idx) => {
                                const profile = c.profile || c.author || c.user || {};
                                const displayName = profile.name || profile.display_name
                                    || profile.handle || profile.username || c.author_name || 'Unknown';
                                const handle = profile.handle || profile.username || c.author_handle || '';
                                const avatar = profile.avatar_url || profile.avatar || profile.image
                                    || c.author_avatar;
                                const profileUrl = profile.profile_url || profile.url || profile.link
                                    || (handle ? profileUrlFor(handle, mention.platform) : null);
                                const text = c.text || c.body || c.content || c.comment || '';
                                const when = c.created_at || c.posted_at || c.timestamp;
                                return (
                                    <li key={c.id || idx} className="bm_comment_row">
                                        <div className="bm_comment_avatar">
                                            {profileUrl ? (
                                                <a href={profileUrl} target="_blank" rel="noopener noreferrer"
                                                    title={`Open ${displayName} in a new tab`}>
                                                    <AccountAvatar handle={handle || displayName}
                                                        platform={mention.platform}
                                                        avatarUrl={avatar} size={36} />
                                                </a>
                                            ) : (
                                                <AccountAvatar handle={handle || displayName}
                                                    platform={mention.platform}
                                                    avatarUrl={avatar} size={36} />
                                            )}
                                        </div>
                                        <div className="bm_comment_body">
                                            <div className="bm_comment_meta">
                                                {/* {profileUrl ? (
                                                    <a href={profileUrl} target="_blank" rel="noopener noreferrer"
                                                        className="bm_comment_name">{displayName}</a>
                                                ) : (
                                                    <span className="bm_comment_name">{displayName}</span>
                                                )} */}
                                                {handle && <span className="bm_comment_handle">@{String(handle).replace(/^@/, '')}</span>}
                                                {when && <span className="bm_comment_time">· {timeAgo(when)}</span>}
                                            </div>
                                            <div className="bm_comment_text">{text}</div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {!loading && !error && pageCount > 1 && (
                        <div className="bm_comments_pagination">
                            <CommonPagination
                                handlePageClick={({ selected }) => setPage(selected)}
                                forcePage={page}
                                pageCount={pageCount}
                            />
                        </div>
                    )}
                </div>
                <div className="bm_modal_foot">
                    <button className="btn btn-sm btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

// Brand24-parity tag palette — used when a tag has no explicit color.
const TAG_PALETTE = ['#3b82f6', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];
function tagColorFor(t, fallback) {
    return t.color || TAG_PALETTE[(t.id || fallback || 0) % TAG_PALETTE.length];
}

// Inline-style helper for the MentionCard ⋯ More popover items.
const menuItemStyle = {
    textAlign: 'left',
    padding: '6px 10px',
    background: 'transparent',
    border: 'none',
    borderRadius: 4,
    color: '#1f2937',
    fontSize: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
};

function MentionCard({ m, onVisitChange, onMute, onTagsChange, onQueueChange, onTakedown, onVerdictChange, onOrgTagsRefresh, orgTags = [] }) {
    const sentiment = (m.sentiment || 'neutral').toLowerCase();
    const sentColor = SENTIMENT_COLOR[sentiment] || SENTIMENT_COLOR.neutral;
    const influence = m.influence_score || 1;
    const sla = misinfoSlaStatus(m);
    const host = hostFromUrl(m.url);
    const [busy, setBusy] = useState(false);
    const [tagPickerOpen, setTagPickerOpen] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [takedownModalOpen, setTakedownModalOpen] = useState(false);
    const [commentsModalOpen, setCommentsModalOpen] = useState(false);
    const [activeEmotions, setActiveEmotions] = useState([]);
    const [activeNarratives, setActiveNarratives] = useState([]);

    const [translation, setTranslation] = useState(m.translated_text || '');
    const [showTranslated, setShowTranslated] = useState(false);
    const [translating, setTranslating] = useState(false);
    const [translateError, setTranslateError] = useState('');
    const lang = (m.language || '').toLowerCase();
    const canTranslate = !!(m.text && lang && lang !== 'en');

    // 2026-05-27 UX cleanup — collapse long bodies + overflow menu.
    const [bodyExpanded, setBodyExpanded] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const BODY_PREVIEW_LEN = 180;
    const appliedTagIds = new Set((m.tags || []).map(t => t.id));
    const takedown = m.takedown;
    const takedownActive = takedown && takedown.status !== 'resolved' && takedown.status !== 'failed';

    const toggleVisited = async () => {
        if (busy) return;
        setBusy(true);
        try {
            if (m.is_visited) await bp_unvisit_mention(m.id);
            else await bp_visit_mention(m.id);
            onVisitChange && onVisitChange(m.id, !m.is_visited);
        } finally { setBusy(false); }
    };

    const setVerdict = async (verdict) => {
        if (busy) return;
        setBusy(true);
        try {
            await bp_set_mention_verdict(m.id, verdict);
            onVerdictChange && onVerdictChange(m.id, verdict);
        } catch (e) {
            console.warn('verdict update failed', e);
        } finally { setBusy(false); }
    };

    const muteHost = async () => {
        if (busy || !host) return;
        if (!window.confirm(`Mute "${host}" for this org? It will be hidden from every Brand Protection feed until you unmute it in Settings.`)) return;
        setBusy(true);
        try {
            await bp_mute_domain({ url: m.url, reason: 'muted from Mentions card' });
            onMute && onMute(host);
        } finally { setBusy(false); }
    };

    const toggleTranslate = async () => {
        if (translation) { setShowTranslated(s => !s); return; }
        if (translating) return;
        setTranslating(true);
        setTranslateError('');
        try {
            const res = await bp_translate_mention(m.id);
            const d = unwrap(res) || {};
            if (d.translated_text) {
                const norm = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
                if (norm(d.translated_text) === norm(m.text)) {
                    setTranslateError(`Already in English (the source platform mislabelled it as ${(m.language || 'unknown').toUpperCase()}).`);
                } else {
                    setTranslation(d.translated_text);
                    setShowTranslated(true);
                }
            } else {
                setTranslateError(d.error === 'translation_unavailable'
                    ? 'NOVA AI is currently unavailable.'
                    : 'Translation failed.');
            }
        } catch (e) {
            setTranslateError('Translation failed.');
        } finally {
            setTranslating(false);
        }
    };

    const toggleTag = async (tag) => {
        if (busy) return;
        setBusy(true);
        try {
            if (appliedTagIds.has(tag.id)) {
                await bp_unapply_mention_tag(m.id, { tag_id: tag.id });
                onTagsChange && onTagsChange(m.id, (m.tags || []).filter(t => t.id !== tag.id));
            } else {
                await bp_apply_mention_tag(m.id, { tag_id: tag.id });
                onTagsChange && onTagsChange(m.id, [...(m.tags || []), tag]);
            }
        } finally { setBusy(false); }
    };

    const createAndApply = async () => {
        const name = newTagName.trim();
        if (!name || busy) return;
        setBusy(true);
        try {
            const res = await bp_apply_mention_tag(m.id, { name });
            // Backend may return the new tag at data.tag, or directly at
            // data, depending on whether this is a create-and-apply vs an
            // apply-existing. Accept either shape so the local state
            // update doesn't silently no-op when only one shape matches.
            const data = unwrap(res) || {};
            const tag = (data.tag && data.tag.id) ? data.tag
                : (data.id ? data : null);
            if (tag) onTagsChange && onTagsChange(m.id, [...(m.tags || []), tag]);
            // Unconditional safety net: even if we couldn't extract the
            // tag object from the response, the toolbar's org-tag list
            // must show the newly-created tag. Re-fetching the canonical
            // list from the backend guarantees that.
            onOrgTagsRefresh && onOrgTagsRefresh();
            setNewTagName('');
        } finally { setBusy(false); }
    };

    const toggleQueue = async () => {
        if (busy) return;
        setBusy(true);
        try {
            if (m.is_in_report_queue) await bp_unqueue_mention(m.id);
            else await bp_queue_mention(m.id, {});
            onQueueChange && onQueueChange(m.id, !m.is_in_report_queue);
        } finally { setBusy(false); }
    };

    const profileUrl = profileUrlFor(m.handle, m.platform);
    // Suppress the risk styling when the customer has explicitly classified
    // this mention as a false positive or chosen to ignore it — but keep
    // the underlying is_risk_tagged_source flag intact so they can undo.
    const showAsRisk = m.is_risk_tagged_source
        && m.customer_verdict !== 'false_positive'
        && m.customer_verdict !== 'ignored';
    return (
        <div className={`bm_card sentiment-${sentiment} ${m.is_visited ? 'is-visited' : ''} ${showAsRisk ? 'is-risk' : ''}`}>
            <div className="bm_card_avatar_col">
                {profileUrl ? (
                    <a href={profileUrl} target="_blank" rel="noopener noreferrer"
                        className="bm_card_avatar_link"
                        title={`Open ${m.handle} on ${m.platform || 'source'}`}>
                        <AccountAvatar handle={m.handle} platform={m.platform} size={44} />
                    </a>
                ) : (
                    <AccountAvatar handle={m.handle} platform={m.platform} size={44} />
                )}
            </div>
            <div className="bm_card_body">
                <div className="bm_card_header_row">
                    <div className="bm_card_identity">
                        {profileUrl ? (
                            <a href={profileUrl} target="_blank" rel="noopener noreferrer"
                                className="bm_handle bm_handle--link"
                                title={`Open ${m.handle} on ${m.platform || 'source'}`}>
                                {m.handle || '(no handle)'}
                                <span className="bm_handle_ext" aria-hidden="true">↗</span>
                            </a>
                        ) : (
                            <span className="bm_handle">{m.handle || '(no handle)'}</span>
                        )}
                        <PlatformChip platform={m.platform} />
                        {m.followers > 0 && m?.platform === "x" && (
                            <span className="bm_followers" title={`${m.followers.toLocaleString()} followers`}>
                                {formatNumber(m.followers)} followers
                            </span>
                        )}
                        {/* {showAsRisk && (
                            <span className="bm_risk_chip" title="Source has been pre-flagged as a risk-tagged account">
                                RISK SOURCE
                            </span>
                        )} */}
                    </div>
                    {m?.platform === "x" && (<span className="bm_date" title={formatLocalDateTime(m.posted_at)}>
                        {timeAgo(m.posted_at)}
                    </span>)}
                </div>
                <div className="bm_card_meta_row">
                    <span className="bm_influence" title={`Influence score (1–10): ${influence}`}>
                        <span className="bm_influence_label">Influence</span>
                        <span className="bm_influence_bar">
                            <span className="bm_influence_fill" style={{ width: `${influence * 10}%` }} />
                        </span>
                        <span className="bm_influence_score">{influence}/10</span>
                    </span>
                    <span className="bm_sentiment_chip"
                        style={{
                            background: sentColor + '18',
                            color: sentColor,
                            border: `1px solid ${sentColor}40`
                        }}
                        title={`Sentiment: ${sentiment}`}>
                        {sentiment}
                    </span>
                    {m.narrative_tag && (
                        <span className="bm_narrative_chip" title={`Narrative cluster: ${m.narrative_tag}`}>
                            {m.narrative_tag.replace(/_/g, ' ')}
                        </span>
                    )}
                    {m.emotion && (
                        <span className={`bm_emotion_chip emo-${m.emotion}`}
                            title={`Emotion: ${m.emotion}`}>
                            {m.emotion}
                        </span>
                    )}
                    {m.language && (
                        <span className="bm_lang_chip"
                            title={`Detected language: ${m.language}`}>
                            {m.language}
                        </span>
                    )}

                    {takedown && (
                        <span className={`bm_takedown_chip status-${takedown.status}`}
                            title={`Takedown ${takedown.status} · ${takedown.target_kind === 'post' ? 'post-level' : 'host-level'}`}>
                            Takedown · {takedown.status}
                        </span>
                    )}
                </div>

                {/* Risk reason + customer verdict controls.
                    Shows when the row has either an active risk flag OR
                    the customer has already acted on it (so they can
                    re-review their own previous false_positive/ignored).
                    On 'false_positive', the classifier suppresses the same
                    flag on subsequent scans for the same publisher+slug. */}


                <div className="bm_card_text">
                    {(() => {
                        const body = (showTranslated && translation) ? translation : (m.text || '');
                        return highlightHashtags(
                            bodyExpanded ? body : body.slice(0, BODY_PREVIEW_LEN)
                        );
                    })()}
                    {!bodyExpanded && ((showTranslated && translation) ? translation : (m.text || '')).length > BODY_PREVIEW_LEN && (
                        <>
                            {'… '}
                            <button type="button"
                                onClick={() => setBodyExpanded(true)}
                                style={{
                                    background: 'none', border: 'none', padding: 0,
                                    color: '#2563eb', cursor: 'pointer',
                                    fontWeight: 500, fontSize: 'inherit',
                                }}
                                title="Show full mention text">
                                Show more
                            </button>
                        </>
                    )}
                    {bodyExpanded && ((showTranslated && translation) ? translation : (m.text || '')).length > BODY_PREVIEW_LEN && (
                        <>
                            {' '}
                            <button type="button"
                                onClick={() => setBodyExpanded(false)}
                                style={{
                                    background: 'none', border: 'none', padding: 0,
                                    color: '#64748b', cursor: 'pointer',
                                    fontWeight: 500, fontSize: 'inherit',
                                }}>
                                Show less
                            </button>
                        </>
                    )}
                </div>
                {canTranslate && (
                    <div style={{
                        marginTop: 6, display: 'flex', gap: 8, alignItems: 'center',
                        fontSize: 11, color: '#64748b',
                    }}>
                        <button type="button"
                            onClick={toggleTranslate}
                            disabled={translating}
                            title={`Source language: ${lang.toUpperCase()}. NOVA AI translates to English on demand; result is cached.`}
                            style={{
                                background: showTranslated ? 'rgba(37,99,235,0.10)' : 'transparent',
                                border: `1px solid ${showTranslated ? '#2563eb' : 'rgba(148,163,184,0.45)'}`,
                                color: showTranslated ? '#1d4ed8' : '#475569',
                                padding: '3px 10px', borderRadius: 999,
                                fontSize: 11, fontWeight: 600,
                                cursor: translating ? 'wait' : 'pointer',
                            }}>
                            {translating
                                ? '⏳ Translating…'
                                : (showTranslated
                                    ? `✓ English (from ${lang.toUpperCase()})`
                                    : `🌐 Translate from ${lang.toUpperCase()}`)}
                        </button>
                        {translation && showTranslated && (
                            <span style={{ fontSize: 10, color: '#94a3b8' }}>
                                NOVA AI
                            </span>
                        )}
                        {translateError && (
                            <span style={{ color: '#b91c1c', fontSize: 11 }}>
                                {translateError}
                            </span>
                        )}
                    </div>
                )}
                {/* {m.url && (
                    <a href={m.url} target="_blank" rel="noopener noreferrer"
                       className="bm_card_link"
                       onClick={() => { if (!m.is_visited) toggleVisited(); }}>
                        {m.url}
                    </a>
                )} */}
                {/* Applied tag chips (rendered below the body text) */}
                {(m.tags || []).length > 0 && (
                    <div className="bm_card_tagchips">
                        {(m.tags || []).map(t => {
                            const c = tagColorFor(t, m.id);
                            return (
                                <span key={t.id} className="bm_card_tagchip"
                                    style={{ background: c + '18', color: c, borderColor: c + '60' }}
                                    title={`Tag · ${t.name} — click × to remove`}>
                                    {t.name}
                                    <button type="button"
                                        onClick={() => toggleTag(t)} disabled={busy}
                                        aria-label={`Remove tag ${t.name}`}
                                        style={{
                                            background: 'transparent', border: 'none',
                                            color: 'inherit', cursor: 'pointer',
                                            padding: '0 0 0 6px', fontSize: 12
                                        }}>
                                        ×
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                )}

                {/* 2026-05-27 UX cleanup — split actions into 3 primary
                    inline + "More" popover for the rest. Primary set
                    chosen for triage flow: Mark read, Review, Takedown. */}
                <div className="bm_card_toolbar">
                    <button type="button"
                        className={`bm_card_tool ${m.is_visited ? 'is-visited' : ''}`}
                        onClick={toggleVisited} disabled={busy}
                        title={m.is_visited ? 'Mark as unread' : 'Mark as read'}>
                        {m.is_visited ? '● Read' : '○ Mark read'}
                    </button>

                    {m.url && (
                        <a href={m.url} target="_blank" rel="noopener noreferrer"
                            className="bm_card_tool bm_card_tool--review"
                            title="Open the post on the source platform in a new tab"
                            onClick={() => { if (!m.is_visited) toggleVisited(); }}>
                            ↗ Review on {m.platform || 'source'}
                        </a>
                    )}
                    {['x', 'bluesky', 'reddit'].includes(m.platform) && (
                        <button type="button"
                            className="bm_card_tool"
                            onClick={() => setCommentsModalOpen(true)}
                            disabled={busy}
                            title="View comments on this post">
                            Show Comments
                        </button>
                    )}
                    {['x'].includes(m.platform) && (
                        <button type="button"
                            className="bm_card_tool-retweet "
                            disabled
                        >
                            {m?.retweet_count > 1 ? `Retweets: ${m.retweet_count}` : `Retweet: ${m.retweet_count}`}
                        </button>
                    )}
                    {m.url && (
                        <button type="button"
                            className={`bm_card_tool bm_card_tool--takedown ${takedownActive ? 'is-active' : ''}`}
                            onClick={() => setTakedownModalOpen(true)}
                            disabled={busy}
                            title={takedownActive
                                ? `Takedown already in flight (status: ${takedown.status}). Click to view.`
                                : 'Request a takedown of this post / host'}>
                            {takedownActive ? '⏱ Takedown · ' + takedown.status : '⚠ Request takedown'}
                        </button>
                    )}
                    <div style={{ position: 'relative', marginLeft: 'auto' }}>
                        <button type="button"
                            className="bm_card_tool"
                            onClick={() => setMoreOpen(o => !o)}
                            disabled={busy}
                            title="More actions"
                            aria-haspopup="true"
                            aria-expanded={moreOpen}>
                            ⋯ More
                        </button>
                        {moreOpen && (
                            <div onMouseLeave={() => setMoreOpen(false)}
                                style={{
                                    position: 'absolute', right: 0, top: 'calc(100% + 4px)',
                                    minWidth: 220, zIndex: 10,
                                    background: '#ffffff',
                                    border: '1px solid rgba(148,163,184,0.45)',
                                    borderRadius: 6,
                                    boxShadow: '0 4px 12px rgba(15,23,42,0.10)',
                                    padding: 6,
                                    display: 'flex', flexDirection: 'column', gap: 2,
                                }}>
                                {/* Tag picker — inline sub-menu */}
                                <button type="button"
                                    onClick={() => setTagPickerOpen(o => !o)}
                                    disabled={busy}
                                    style={menuItemStyle}>
                                    {tagPickerOpen ? '▾ Tag' : '+ Tag'}
                                </button>
                                {tagPickerOpen && (
                                    <div className="bm_card_tag_panel"
                                        style={{ position: 'static', marginTop: 0, marginBottom: 4 }}>
                                        {orgTags.length > 0 && (
                                            <div className="bm_card_tag_list">
                                                {orgTags.map(t => {
                                                    const on = appliedTagIds.has(t.id);
                                                    const c = tagColorFor(t, t.id);
                                                    return (
                                                        <button key={t.id}
                                                            type="button"
                                                            className={`bm_card_tag_opt ${on ? 'on' : ''}`}
                                                            onClick={() => toggleTag(t)}
                                                            disabled={busy}>
                                                            <span style={{
                                                                display: 'inline-block',
                                                                width: 8, height: 8, borderRadius: 2,
                                                                background: c, marginRight: 6
                                                            }} />
                                                            {t.name}
                                                            {on && <span style={{ marginLeft: 'auto' }}>✓</span>}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <div className="bm_card_tag_new">
                                            <input type="text" placeholder="New tag name…"
                                                value={newTagName}
                                                onChange={e => setNewTagName(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') createAndApply(); }} />
                                            <button type="button" className="btn btn-sm btn-primary"
                                                onClick={createAndApply}
                                                disabled={busy || !newTagName.trim()}>
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <button type="button"
                                    onClick={() => { toggleQueue(); setMoreOpen(false); }}
                                    disabled={busy}
                                    style={menuItemStyle}
                                    title={m.is_in_report_queue
                                        ? 'Remove from report queue'
                                        : 'Add to the next PDF/Excel/CSV report'}>
                                    {m.is_in_report_queue ? '✓ In report' : '+ Add to report'}
                                </button>
                                {/* {host && (
                                    <button type="button"
                                            onClick={() => { muteHost(); setMoreOpen(false); }}
                                            disabled={busy}
                                            style={menuItemStyle}
                                            title={`Mute every future mention from ${host}`}>
                                        🔇 Mute {host}
                                    </button>
                                )} */}
                            </div>
                        )}
                    </div>
                </div>
                {takedownModalOpen && (
                    <TakedownModal
                        mention={m}
                        onClose={() => setTakedownModalOpen(false)}
                        onSubmitted={(mid, td) => {
                            onTakedown && onTakedown(mid, td);
                        }} />
                )}
                {commentsModalOpen && (
                    <CommentsModal
                        mention={m}
                        onClose={() => setCommentsModalOpen(false)} />
                )}
            </div>
        </div>
    );
}

export default function MentionsTab() {
    const LS_KEY = 'bm_saved_filter_v1';
    const [days, setDays] = useState(30);
    const [bucket, setBucket] = useState('days');
    const [chartMode, setChartMode] = useState('mentions_reach');
    const [platforms, setPlatforms] = useState([]);
    const [sentiments, setSentiments] = useState([]);
    const [riskOnly, setRiskOnly] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sort, setSort] = useState('-posted_at');
    const [refreshTick, setRefreshTick] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    // 2026-05-26 — Scan now (dispatches brand_mention_scan) + status
    // indicator showing the customer their pending edits aren't live
    // until the next scan. Refresh ≠ Scan: Refresh re-fetches the
    // mentions API; Scan re-runs the underlying brand_mention_scan task
    // that hits Reddit / X / Facebook / LinkedIn / etc.
    const [scanBusy, setScanBusy] = useState(false);
    const [scanBanner, setScanBanner] = useState(null);
    const [scanStatus, setScanStatus] = useState(null);
    // Poll handle for the scan-status loop kicked off by "Scan now".
    // Cleared when a newer last_scan_at lands, on safety timeout, or unmount.
    const scanPollRef = useRef(null);
    const scanPollTimeoutRef = useRef(null);
    // CEPI RFP #28 — alert-latency SLA pill (p95 + 24h SLA compliance over
    // the last 30 days). Lives in the header next to the Monitoring pill.
    const [alertLatency, setAlertLatency] = useState(null);
    // New-since-last-view delta — pulled from localStorage on mount,
    // re-pulled after Scan now / Refresh, written when the user closes
    // the tab or scrolls past page 1 (treating that as "they engaged").
    const [newSinceCount, setNewSinceCount] = useState(0);
    const [savedSnapshot, setSavedSnapshot] = useState(null);
    // Brand24-parity slice 2 — Visited filter (all/read/unread).
    const [visitedMode, setVisitedMode] = useState('all');
    // Brand24-parity slice 2 — Tag taxonomy + active tag filter (IDs).
    const [orgTags, setOrgTags] = useState([]);
    const [activeTagIds, setActiveTagIds] = useState([]);
    // Brand24-parity slice 2-C — PDF report queue.
    const [queueCount, setQueueCount] = useState(0);
    const [showQueueOnly, setShowQueueOnly] = useState(false);
    // Brand24-parity slice 3 — right-rail filters.
    const [importanceMode, setImportanceMode] = useState('all'); // all|important
    const [influenceMin, setInfluenceMin] = useState('');       // numeric string
    const [influenceMax, setInfluenceMax] = useState('');
    // Slice 3-B — language + emotion multi-select.
    const [activeLanguages, setActiveLanguages] = useState([]);
    const [activeEmotions, setActiveEmotions] = useState([]);
    const [activeNarratives, setActiveNarratives] = useState([]);
    // Spike drill-in (slice 3-D) — selected marker + date-bucket filter.
    const [selectedSpike, setSelectedSpike] = useState(null);
    const [bucketFilter, setBucketFilter] = useState({ from: '', to: '' });
    // Topic card drill-in (slice 3-E) — when an analyst clicks a card on
    // Topic Analysis, that tab fires `bm-mentions-set-filter` with either
    // {narrative} (auto-detected) or {monitored_topic} (user-defined).
    const [monitoredTopic, setMonitoredTopic] = useState('');
    const [narrativeFilter, setNarrativeFilter] = useState('');

    // Hydrate from localStorage if a saved filter set exists.
    useEffect(() => {
        try {
            const raw = localStorage.getItem(LS_KEY);
            if (raw) {
                const f = JSON.parse(raw);
                setSavedSnapshot(f);
            }
        } catch (_) { /* ignore */ }
    }, []);

    // Topic card drill-in listener — payload is {narrative} or
    // {monitored_topic}. Setting either updates filterParams via state.
    useEffect(() => {
        const handler = (e) => {
            const d = e.detail || {};
            // Clear other potentially-conflicting filters so the analyst
            // sees exactly the topic/author/platform's mentions, not the
            // intersection with leftover state from the previous filter.
            if (d.narrative) {
                setNarrativeFilter(d.narrative);
                setMonitoredTopic('');
            }
            if (d.monitored_topic) {
                setMonitoredTopic(d.monitored_topic);
                setNarrativeFilter('');
            }
            // Influencer/Sources drill-in (slice 3-F).
            if (d.author) {
                // Reuse `search` for author drill-in — backend's iexact
                // is handled at the queryset level via the `?author=`
                // param; here we just need the visible state to reflect
                // it so the analyst sees what's filtering the feed.
                setSearch(d.author);
            }
            if (d.platform) {
                setPlatforms([(d.platform || '').toLowerCase()]);
            }
            setBucketFilter({ from: '', to: '' });
            setPage(1);
        };
        window.addEventListener('bm-mentions-set-filter', handler);
        return () => window.removeEventListener('bm-mentions-set-filter', handler);
    }, []);

    const filtersAreCustom = (
        platforms.length > 0 || sentiments.length > 0 ||
        riskOnly || search.length > 0 || days !== 30 || sort !== '-posted_at' ||
        activeLanguages.length > 0 || activeEmotions.length > 0 ||
        activeNarratives.length > 0 || activeTagIds.length > 0 ||
        showQueueOnly || visitedMode !== 'all' || importanceMode !== 'all' ||
        influenceMin !== '' || influenceMax !== '' ||
        !!bucketFilter.from || !!bucketFilter.to ||
        !!monitoredTopic || !!narrativeFilter
    );

    const clearFilters = () => {
        setPlatforms([]); setSentiments([]); setRiskOnly(false);
        setSearch(''); setDays(30); setSort('-posted_at'); setPage(1);
        setActiveLanguages([]); setActiveEmotions([]); setActiveNarratives([]);
        setActiveTagIds([]); setShowQueueOnly(false);
        setVisitedMode('all'); setImportanceMode('all');
        setInfluenceMin(''); setInfluenceMax('');
        setBucketFilter({ from: '', to: '' });
        setMonitoredTopic(''); setNarrativeFilter('');
    };

    const saveFilters = () => {
        const snapshot = {
            days, platforms, sentiments, riskOnly, search, sort,
            activeLanguages, activeEmotions, activeNarratives, activeTagIds,
            showQueueOnly, visitedMode, importanceMode,
            influenceMin, influenceMax, bucketFilter,
            monitoredTopic, narrativeFilter,
        };
        try { localStorage.setItem(LS_KEY, JSON.stringify(snapshot)); } catch (_) { }
        setSavedSnapshot(snapshot);
    };

    const loadSavedFilters = () => {
        if (!savedSnapshot) return;
        setDays(savedSnapshot.days ?? 30);
        setPlatforms(savedSnapshot.platforms || []);
        setSentiments(savedSnapshot.sentiments || []);
        setRiskOnly(!!savedSnapshot.riskOnly);
        setSearch(savedSnapshot.search || '');
        setSort(savedSnapshot.sort || '-posted_at');
        setActiveLanguages(savedSnapshot.activeLanguages || []);
        setActiveEmotions(savedSnapshot.activeEmotions || []);
        setActiveNarratives(savedSnapshot.activeNarratives || []);
        setActiveTagIds(savedSnapshot.activeTagIds || []);
        setShowQueueOnly(!!savedSnapshot.showQueueOnly);
        setVisitedMode(savedSnapshot.visitedMode || 'all');
        setImportanceMode(savedSnapshot.importanceMode || 'all');
        setInfluenceMin(savedSnapshot.influenceMin ?? '');
        setInfluenceMax(savedSnapshot.influenceMax ?? '');
        setBucketFilter(savedSnapshot.bucketFilter || { from: '', to: '' });
        setMonitoredTopic(savedSnapshot.monitoredTopic || '');
        setNarrativeFilter(savedSnapshot.narrativeFilter || '');
        setPage(1);
    };

    const forgetSaved = () => {
        try { localStorage.removeItem(LS_KEY); } catch (_) { }
        setSavedSnapshot(null);
    };

    const [feed, setFeed] = useState({ results: [], total: 0, pages: 0 });
    const [feedLoading, setFeedLoading] = useState(false);
    const [chart, setChart] = useState({ mentions_reach_series: [], sentiment_series: [] });
    const [summary, setSummary] = useState({ total: 0, by_sentiment: {}, by_narrative: {} });
    const [influencers, setInfluencers] = useState([]);
    const [sources, setSources] = useState([]);
    const [filterOptions, setFilterOptions] = useState({ platforms: [], sentiments: [] });
    console.log("foooo", filterOptions);

    const [undiscovered, setUndiscovered] = useState({ items: [], dismissed: false });
    const [aiPanel, setAiPanel] = useState(null);   // {state, text} | null

    const filterParams = useMemo(() => ({
        days,
        platform: platforms.join(',') || undefined,
        sentiment: sentiments.join(',') || undefined,
        risk_only: riskOnly ? 1 : undefined,
        search: search || undefined,
        visited: visitedMode === 'all' ? undefined : visitedMode,
        tag_ids: activeTagIds.length ? activeTagIds.join(',') : undefined,
        from_queue: showQueueOnly ? 1 : undefined,
        importance: importanceMode === 'all' ? undefined : importanceMode,
        influence_min: influenceMin === '' ? undefined : Number(influenceMin),
        influence_max: influenceMax === '' ? undefined : Number(influenceMax),
        language: activeLanguages.length ? activeLanguages.join(',') : undefined,
        emotion: activeEmotions.length ? activeEmotions.join(',') : undefined,
        narrative: activeNarratives.length ? activeNarratives.join(',') : undefined,
        from_date: bucketFilter.from || undefined,
        to_date: bucketFilter.to || undefined,
        monitored_topic: monitoredTopic || undefined,
        // narrative has two sources: `narrativeFilter` is set when the user
        // drills in from a topic card (single value), and `activeNarratives`
        // is the multi-select rail filter. Merge both into a CSV so either
        // path actually narrows the feed.
        narrative: [narrativeFilter, ...activeNarratives].filter(Boolean).join(',') || undefined,
    }), [days, platforms, sentiments, riskOnly, search, visitedMode, activeTagIds, showQueueOnly,
        importanceMode, influenceMin, influenceMax, activeLanguages, activeEmotions, activeNarratives,
        bucketFilter, monitoredTopic, narrativeFilter]);

    const refreshOrgTags = () => {
        bp_list_mention_tags()
            .then(r => setOrgTags(((unwrap(r) || {}).items) || []))
            .catch(() => { });
    };
    const deleteOrgTag = async (tag) => {
        if (!window.confirm(`Delete tag "${tag.name}"? It will be removed from every mention that currently has it.`)) return;
        try {
            await bp_delete_mention_tag(tag.id);
        } catch (_) {
            window.alert('Could not delete tag — try again.');
            return;
        }
        setOrgTags(prev => prev.filter(t => t.id !== tag.id));
        setActiveTagIds(prev => prev.filter(id => id !== tag.id));
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r => ({
                ...r,
                tags: (r.tags || []).filter(x => x.id !== tag.id),
            })),
        }));
    };
    const refreshQueueCount = () => {
        bp_list_report_queue()
            .then(r => setQueueCount(((unwrap(r) || {}).total) || 0))
            .catch(() => { });
    };

    // Clear any in-flight scan-status polling on unmount.
    useEffect(() => () => {
        if (scanPollRef.current) { clearInterval(scanPollRef.current); scanPollRef.current = null; }
        if (scanPollTimeoutRef.current) { clearTimeout(scanPollTimeoutRef.current); scanPollTimeoutRef.current = null; }
    }, []);

    useEffect(() => {
        bp_filters().then(res => setFilterOptions(unwrap(res) || {})).catch(() => { });
        refreshOrgTags();
        refreshQueueCount();
        bp_scan_status().then(res => setScanStatus(unwrap(res) || null)).catch(() => { });
        bp_alert_latency({ days: 30 })
            .then(res => setAlertLatency(unwrap(res) || null))
            .catch(() => { });
        // New-since-last-view: read the stored timestamp (per-org) and
        // ask the backend how many rows landed after it. Don't overwrite
        // the stored timestamp until the user explicitly engages with
        // the feed — that way someone bouncing between tabs all day
        // still sees "12 new" until they actually look.
        try {
            const orgId = localStorage.getItem('organization_id') || '0';
            const key = `bm_last_viewed_at_org_${orgId}`;
            const since = localStorage.getItem(key);
            if (since) {
                bp_mentions_since({ since })
                    .then(res => {
                        const d = unwrap(res) || {};
                        setNewSinceCount(d.count || 0);
                    })
                    .catch(() => { });
            } else {
                // First visit ever — seed the timestamp to now, no delta
                // to show yet.
                localStorage.setItem(key, new Date().toISOString());
            }
        } catch { }
        // Brand24-parity: surface providers NMT has wired up but this
        // tenant isn't entitled to yet. Backend already excludes anything
        // the org has access to, so a non-empty list = legitimate gap.
        const dismissed = localStorage.getItem('bm_undiscovered_dismiss') === '1';
        if (!dismissed) {
            bp_undiscovered_sources()
                .then(res => {
                    const body = unwrap(res) || {};
                    setUndiscovered({
                        items: body.items || [],
                        contact_email: body.contact_email,
                        dismissed: false,
                    });
                })
                .catch(() => { });
        } else {
            setUndiscovered({ items: [], dismissed: true });
        }
    }, []);

    const dismissUndiscovered = () => {
        try { localStorage.setItem('bm_undiscovered_dismiss', '1'); } catch (_) { }
        setUndiscovered(u => ({ ...u, dismissed: true }));
    };

    const runSummarize = async () => {
        setAiPanel({ state: 'loading', text: '' });
        const filterDesc = [
            `last ${days} days`,
            platforms.length ? `platforms: ${platforms.join(', ')}` : null,
            sentiments.length ? `sentiment: ${sentiments.join(', ')}` : null,
            riskOnly ? 'risk-tagged only' : null,
            search ? `matching "${search}"` : null,
        ].filter(Boolean).join('; ');
        try {
            const res = await bp_assistant_ask({
                question: `Summarise the brand mentions in scope (${filterDesc}). `
                    + 'Cover: total volume, sentiment split, the dominant '
                    + 'narratives, and any spikes or risk-tagged sources '
                    + 'worth flagging. Keep it tight — 5 bullets max.',
                thread: [],
            });
            const data = unwrap(res) || {};
            setAiPanel({
                state: 'done',
                text: data.answer || 'No summary returned.',
                provider: data.provider
            });
        } catch (e) {
            setAiPanel({
                state: 'error',
                text: 'AI summary is temporarily unavailable. Try again in a moment.'
            });
        }
    };

    const generateReport = () => {
        // The Infographic tab is the print-ready report surface; hand off
        // there rather than building a parallel PDF pipeline.
        window.dispatchEvent(new CustomEvent('bm-tab-switch',
            { detail: { tab: 'infographic' } }));
    };

    useEffect(() => {
        const t = setTimeout(() => {
            setFeedLoading(true);
            Promise.allSettled([
                bp_mentions({ ...filterParams, page, sort, page_size: pageSize }),
                bp_chart({ ...filterParams, bucket }),
                bp_sentiment(filterParams),
                bp_influencers({ ...filterParams, limit: 10 }),
                bp_sources(filterParams),
            ]).finally(() => { setFeedLoading(false); setRefreshing(false); }).then(([m, c, s, i, src]) => {
                const ok = r => r?.status === 'fulfilled' ? r.value : null;
                [m, c, s, i, src] = [ok(m), ok(c), ok(s), ok(i), ok(src)];
                setFeed(unwrap(m));
                // 2026-05-27 — clamp chart series to the active days
                // window. Backend's "freshness" filter is on created_at,
                // but the chart buckets by posted_at, so a Reddit
                // archive captured today with posted_at=2020 was
                // stretching the x-axis across 6 years. Visual-only
                // clamp; the underlying data is intact.
                //
                // The bucket label format depends on `bucket`:
                //   days   → YYYY-MM-DD   weeks → YYYY-Www (ISO)   months → YYYY-MM
                // Date.parse only understands the day form — "2026-W21"
                // and "2026-05" returned NaN, so weekly/monthly buckets
                // were silently dropped and those views rendered empty.
                // Parse each format explicitly, and snap the cutoff to the
                // start of its own week/month so the boundary bucket that
                // only partially overlaps the window isn't dropped.
                const chartData = unwrap(c) || {};
                const bucketStartMs = (label) => {
                    if (!label) return NaN;
                    const wk = /^(\d{4})-W(\d{2})$/.exec(label);
                    if (wk) {
                        const year = +wk[1], week = +wk[2];
                        // Monday of ISO week 1 (the week containing Jan 4).
                        const jan4Mon = (new Date(Date.UTC(year, 0, 4)).getUTCDay() + 6) % 7;
                        return Date.UTC(year, 0, 4) - jan4Mon * 86400000 + (week - 1) * 7 * 86400000;
                    }
                    const mo = /^(\d{4})-(\d{2})$/.exec(label);
                    if (mo) return Date.UTC(+mo[1], +mo[2] - 1, 1);
                    const t = Date.parse(label);
                    return Number.isFinite(t) ? t : NaN;
                };
                let cutoffMs = Date.now() - (days * 86400 * 1000);
                if (bucket === 'months') {
                    const c0 = new Date(cutoffMs);
                    cutoffMs = Date.UTC(c0.getUTCFullYear(), c0.getUTCMonth(), 1);
                } else if (bucket === 'weeks') {
                    const c0 = new Date(cutoffMs);
                    const isoMon = (c0.getUTCDay() + 6) % 7;
                    cutoffMs = Date.UTC(c0.getUTCFullYear(), c0.getUTCMonth(), c0.getUTCDate()) - isoMon * 86400000;
                }
                const inRange = (b) => {
                    const t = bucketStartMs(b && b.bucket);
                    return Number.isFinite(t) && t >= cutoffMs;
                };
                setChart({
                    ...chartData,
                    mentions_reach_series: (chartData.mentions_reach_series || []).filter(inRange),
                    sentiment_series: (chartData.sentiment_series || []).filter(inRange),
                });
                setSummary(unwrap(s));
                setInfluencers((unwrap(i) || {}).influencers || []);
                setSources((unwrap(src) || {}).sources || []);
            });
        }, search ? 300 : 0);
        return () => clearTimeout(t);
    }, [filterParams, page, pageSize, sort, bucket, refreshTick]);

    const toggleInList = (val, list, setter) => {
        const v = val.toLowerCase();
        setter(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);
        setPage(1);
    };

    // Brand24-parity slice 2 — local handlers so cards can update visited /
    // muted state without re-fetching the whole feed page.
    const handleVisitChange = (mentionId, nextVisited) => {
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r =>
                r.id === mentionId ? { ...r, is_visited: nextVisited } : r
            ),
        }));
    };
    const handleVerdictChange = (mentionId, verdict) => {
        // Don't mutate is_risk_tagged_source — that's the backend's
        // immutable signal that the source itself was flagged. The risk
        // badge is hidden/shown based on customer_verdict in the render
        // path, which means undoing a verdict back to 'unreviewed' brings
        // the panel and the RISK SOURCE chip back as expected.
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r =>
                r.id === mentionId ? { ...r, customer_verdict: verdict } : r
            ),
        }));
    };
    const handleTakedownSubmitted = (mentionId, td) => {
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r =>
                r.id === mentionId ? { ...r, takedown: td } : r
            ),
        }));
    };
    const handleQueueChange = (mentionId, nextQueued) => {
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r =>
                r.id === mentionId ? { ...r, is_in_report_queue: nextQueued } : r
            ),
        }));
        setQueueCount(c => Math.max(0, c + (nextQueued ? 1 : -1)));
    };
    const clearQueue = async () => {
        if (!queueCount) return;
        if (!window.confirm(`Clear all ${queueCount} queued mentions? This empties the report queue but does not delete the mentions themselves.`)) return;
        await bp_clear_report_queue();
        setQueueCount(0);
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r => ({ ...r, is_in_report_queue: false })),
        }));
    };
    const handleTagsChange = (mentionId, nextTags) => {
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r =>
                r.id === mentionId ? { ...r, tags: nextTags } : r
            ),
        }));
        // Optimistically merge any newly-created tag into the org-tag list
        // so the toolbar chip appears immediately. refreshOrgTags() below
        // is the eventual-consistency safety net.
        setOrgTags(prev => {
            const known = new Set(prev.map(t => t.id));
            const additions = (nextTags || []).filter(t => t && t.id && !known.has(t.id));
            return additions.length ? [...prev, ...additions] : prev;
        });
        refreshOrgTags();
    };
    const handleMuteHost = (host) => {
        const sameHost = (url) => {
            try { return new URL(url).hostname.replace(/^www\./, '') === host; }
            catch (_) { return false; }
        };
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).filter(r => !sameHost(r.url)),
            total: Math.max(0, (prev.total || 0)
                - (prev.results || []).filter(r => sameHost(r.url)).length),
        }));
    };

    return (
        <>
            <FullPageLoader loading={refreshing} />
            <div className="bm_header">
                <div className="bm_subtitle">
                    <span style={{ marginRight: '4px' }}> {summary.total ?? feed.total} mentions in the last {days} days</span>
                    {typeof feed.total_all_time === 'number' && feed.total_all_time !== feed.total && (
                        <span style={{
                            marginLeft: 8, fontSize: 12, color: '#64748b',
                            fontWeight: 400,
                        }}>
                            · {feed.total_all_time} captured all-time
                        </span>
                    )}
                    {typeof feed.total_all_time === 'number'
                        && feed.total_all_time === feed.total
                        && feed.total > 0 && (
                            <span style={{
                                marginLeft: 8, fontSize: 10, fontWeight: 700, marginRight: 4,
                                color: '#475569',
                                background: 'rgba(148, 163, 184, 0.12)',
                                border: '1px solid rgba(148, 163, 184, 0.3)',
                                padding: '2px 8px', borderRadius: 4,
                                letterSpacing: 0.4, textTransform: 'uppercase',
                            }} title={`Every captured mention for this org fits inside the ` +
                                `last ${days} days — widening the lookback won't pull more in. ` +
                                `New mentions will appear as the scanner captures them.`}>
                                All captured
                            </span>
                        )}
                </div>
                <div className="bm_search_wrap">
                    <input type="search"
                        placeholder="Search through mentions, authors & domains"
                        className="bm_search"
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }} />
                </div>
                {/* Brand24-parity: AI is always one click away from the data
                    the user is looking at. Summarize runs the assistant on
                    the active filter set; Generate Report hands off to the
                    Infographic tab (the existing print-ready surface). */}
                <div className="bm_ai_actions">
                    {queueCount > 0 && (
                        <button type="button"
                            className={`bm_ai_btn bm_queue_btn ${showQueueOnly ? 'active' : ''}`}
                            onClick={() => { setShowQueueOnly(v => !v); setPage(1); }}
                            title={showQueueOnly
                                ? 'Showing only queued mentions — click to see all'
                                : 'Show only mentions in your report queue'}>
                            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                                <rect x="3" y="3" width="10" height="10" rx="1.5"
                                    fill="none" stroke="currentColor" strokeWidth="1.4" />
                                <polyline points="5.5,8 7.5,10 10.5,6"
                                    fill="none" stroke="currentColor" strokeWidth="1.4"
                                    strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Report queue · {queueCount}
                        </button>
                    )}
                    {queueCount > 0 && (
                        <button type="button"
                            className="bm_ai_btn"
                            onClick={clearQueue}
                            title="Empty the queue">
                            Clear
                        </button>
                    )}
                    <button type="button" className="bm_ai_btn bm_ai_btn--purple"
                        onClick={runSummarize}
                        disabled={aiPanel?.state === 'loading'}>
                        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                            <circle cx="8" cy="8" r="6.5" fill="none"
                                stroke="#7c3aed" strokeWidth="1.4" />
                            <circle cx="8" cy="8" r="2.2" fill="#7c3aed" />
                        </svg>
                        {aiPanel?.state === 'loading' ? 'Summarising…' : 'Summarize with AI'}
                    </button>
                    <button type="button" className="bm_ai_btn bm_ai_btn--green"
                        onClick={generateReport}>
                        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                            <rect x="3" y="2.5" width="10" height="11" rx="1"
                                fill="none" stroke="#16a34a" strokeWidth="1.4" />
                            <line x1="5.5" y1="5.5" x2="10.5" y2="5.5"
                                stroke="#16a34a" strokeWidth="1.4" />
                            <line x1="5.5" y1="8" x2="10.5" y2="8"
                                stroke="#16a34a" strokeWidth="1.4" />
                            <line x1="5.5" y1="10.5" x2="8" y2="10.5"
                                stroke="#16a34a" strokeWidth="1.4" />
                        </svg>
                        Generate Report
                    </button>
                </div>
            </div>

            {undiscovered.items.length > 0 && !undiscovered.dismissed && (
                <div className="bm_upsell">
                    <div className="bm_upsell_body">
                        <strong>You have undiscovered mentions on{' '}
                            {undiscovered.items.length === 1
                                ? undiscovered.items[0].label
                                : `${undiscovered.items.length} additional sources`}.
                        </strong>
                        <p>
                            {undiscovered.items.length === 1
                                ? undiscovered.items[0].blurb + '. '
                                : 'Activate them to extend mention coverage across '
                                + undiscovered.items.map(i => i.label).join(' · ') + '. '}
                            Your current contract doesn’t include{' '}
                            {undiscovered.items.length === 1 ? 'this source' : 'these sources'} yet.
                        </p>
                    </div>
                    <div className="bm_upsell_actions">
                        <a className="btn btn-sm btn-primary"
                            href={`mailto:${undiscovered.contact_email || 'sales@nmtsecurity.com'}`
                                + `?subject=Request%20access%20-%20${encodeURIComponent(
                                    undiscovered.items.map(i => i.label).join(', '))}`
                                + `&body=${encodeURIComponent('Hi NMT — please enable the following data sources for our tenant:\n\n'
                                    + undiscovered.items.map(i => '• ' + i.label).join('\n'))}`}>
                            Request access
                        </a>
                        <button type="button" className="bm_upsell_dismiss"
                            onClick={dismissUndiscovered}
                            title="Hide this message in this browser">
                            Hide
                        </button>
                    </div>
                </div>
            )}

            {aiPanel && (
                <div className="bm_ai_panel">
                    <div className="bm_ai_panel_head">
                        <span className="bm_ai_panel_title">
                            <svg width="14" height="14" viewBox="0 0 32 32" aria-hidden="true">
                                <circle cx="16" cy="16" r="12" fill="none" stroke="#7c3aed" strokeWidth="2.4" opacity="0.55" />
                                <circle cx="16" cy="16" r="6" fill="none" stroke="#7c3aed" strokeWidth="2.4" />
                                <circle cx="16" cy="16" r="2" fill="#7c3aed" />
                            </svg>
                            NOVA AI — Summary of your current filter set
                        </span>
                        <button type="button" onClick={() => setAiPanel(null)}
                            aria-label="Close AI summary">×</button>
                    </div>
                    <div className="bm_ai_panel_body">
                        {aiPanel.state === 'loading' && (
                            <>
                                <Skeleton height={12} width="90%" style={{ marginBottom: 8 }} />
                                <Skeleton height={12} width="75%" style={{ marginBottom: 8 }} />
                                <Skeleton height={12} width="82%" />
                            </>
                        )}
                        {aiPanel.state === 'error' && (
                            <div style={{ color: '#b91c1c', fontSize: 13 }}>{aiPanel.text}</div>
                        )}
                        {aiPanel.state === 'done' && (
                            <div className="bm_md"><ReactMarkdown>{mdNormalize(aiPanel.text)}</ReactMarkdown></div>
                        )}
                    </div>
                    {aiPanel.state === 'done' && (
                        <div className="bm_ai_panel_footer">
                            Generated by NOVA AI · grounded in your scan data
                        </div>
                    )}
                </div>
            )}

            <div className="bm_filters">
                <select className="bm_days" value={days}
                    onChange={e => { setDays(Number(e.target.value)); setPage(1); }}>
                    <option value={1}>Last 24 hours</option>
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={365}>Last 12 months</option>
                </select>
                <select className="bm_sort" value={sort}
                    onChange={e => { setSort(e.target.value); setPage(1); }}>
                    <option value="-posted_at">Newest</option>
                    <option value="posted_at">Oldest</option>
                    <option value="relevance">By relevance (reach)</option>
                    <option value="-reach_score">Highest reach</option>
                </select>
                {/* <label className="bm_chip_toggle">
                    <input type="checkbox" checked={riskOnly}
                        onChange={e => { setRiskOnly(e.target.checked); setPage(1); }} />
                    Risk-tagged only
                </label> */}
                {/* Visited filter moved to the right rail (slice 3). Tag filter
                    chips and Save/Refresh actions stay in the top strip. */}
                {/* Tag filter chips — light up active tags; click toggles. */}
                {orgTags.map(t => {
                    const on = activeTagIds.includes(t.id);
                    const c = tagColorFor(t, t.id);
                    return (
                        <span key={'tag-' + t.id}
                            className={`bm_chip bm_chip_tag ${on ? 'active' : ''}`}
                            style={on
                                ? {
                                    borderColor: c, color: c, background: c + '12',
                                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 6px 3px 10px'
                                }
                                : {
                                    borderColor: c + '60', color: c,
                                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 6px 3px 10px'
                                }}>
                            <button type="button"
                                onClick={() => {
                                    setActiveTagIds(prev => prev.includes(t.id)
                                        ? prev.filter(x => x !== t.id)
                                        : [...prev, t.id]);
                                    setPage(1);
                                }}
                                style={{
                                    background: 'transparent', border: 'none',
                                    color: 'inherit', cursor: 'pointer', padding: 0,
                                    font: 'inherit'
                                }}
                                title={on ? `Click to remove "${t.name}" filter` : `Click to filter by "${t.name}"`}>
                                {on ? '● ' : ''}{t.name}
                            </button>
                            <button type="button"
                                onClick={(e) => { e.stopPropagation(); deleteOrgTag(t); }}
                                style={{
                                    background: 'transparent', border: 'none',
                                    color: 'inherit', cursor: 'pointer',
                                    padding: '0 2px', fontSize: 13, lineHeight: 1,
                                    opacity: 0.7
                                }}
                                aria-label={`Delete tag ${t.name}`}
                                title={`Delete tag "${t.name}" (removes it from every mention org-wide)`}>
                                ×
                            </button>
                        </span>
                    );
                })}
                {/* 2026-05-27 UX cleanup — Platform + Sentiment chips moved
                    out of the top toolbar to remove duplication with the
                    right-rail Sources + Sentiment cards (both now
                    filter-on-click). Top toolbar keeps only the
                    not-rail-able controls (Days / Sort / Risk-tagged /
                    Tag chips) plus the action buttons on the right. */}
                {/* Brand24-parity actions: Clear / Save / (Load saved) / Refresh */}
                <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
                    {filtersAreCustom && (
                        <button className="bm_chip" onClick={clearFilters}
                            title="Reset all filters to defaults">
                            ✕ Clear filters
                        </button>
                    )}
                    <button className="bm_chip" onClick={saveFilters}
                        title="Save current filter set to this browser (localStorage)"
                        disabled={!filtersAreCustom}
                        style={{ opacity: filtersAreCustom ? 1 : 0.55 }}>
                        + Save filters
                    </button>
                    {savedSnapshot && (
                        <>
                            <button className="bm_chip" onClick={loadSavedFilters}
                                title="Apply your saved filter set"
                                style={{ borderColor: '#3b82f6', color: '#3b82f6' }}>
                                ↻ Apply saved
                            </button>
                            <button className="bm_chip" onClick={forgetSaved}
                                title="Forget the saved filter set">×</button>
                        </>
                    )}
                    {/* Refresh — light-weight, re-fetches mentions list only. */}
                    <button type="button"
                        onClick={() => { setRefreshing(true); setRefreshTick(t => t + 1); }}
                        title="Re-fetch the latest mentions from the API (does not trigger a new scan)"
                        style={{
                            padding: '6px 12px', borderRadius: 6,
                            border: '1px solid rgba(16,185,129,0.45)',
                            background: 'rgba(16,185,129,0.10)',
                            color: '#047857', fontWeight: 600, fontSize: 12,
                            cursor: 'pointer', whiteSpace: 'nowrap',
                        }}>
                        ↺ Refresh
                    </button>
                    {/* Scan now — primary action, distinct from chips. */}
                    <button type="button"
                        disabled={scanBusy}
                        onClick={async () => {
                            setScanBusy(true);
                            setScanBanner(null);
                            const baselineScanAt = scanStatus?.last_scan_at || null;
                            try {
                                const res = await bp_rescan_mentions();
                                const sc = res?.statusCode || res || {};
                                if (sc.status === 0 && sc.error === 'rate_limited') {
                                    setScanBanner({
                                        tone: 'amber',
                                        text: formatRateLimitedMessage(sc.message, scanStatus?.cadence_minutes),
                                    });
                                } else {
                                    const d = (sc?.response_data?.data || sc?.data || res?.data || {});
                                    setScanBanner({
                                        tone: 'green',
                                        text: d.dispatched
                                            ? `Scan dispatched :  Mentions will refresh in a few minutes.`
                                            : 'Scan acknowledged but not dispatched — check server logs.',
                                    });
                                    if (d.dispatched) {
                                        // Kick off scan-status polling: first call immediately,
                                        // then every 2 min until last_scan_at advances past the
                                        // baseline (= the dispatched scan finished). Hard-stop
                                        // after 30 minutes so a hung task can't poll forever.
                                        if (scanPollRef.current) clearInterval(scanPollRef.current);
                                        if (scanPollTimeoutRef.current) clearTimeout(scanPollTimeoutRef.current);
                                        const stopPolling = () => {
                                            if (scanPollRef.current) { clearInterval(scanPollRef.current); scanPollRef.current = null; }
                                            if (scanPollTimeoutRef.current) { clearTimeout(scanPollTimeoutRef.current); scanPollTimeoutRef.current = null; }
                                        };
                                        const pollOnce = async () => {
                                            try {
                                                const sres = await bp_scan_status();
                                                const sdata = unwrap(sres) || null;
                                                setScanStatus(sdata);
                                                if (sdata && sdata.last_scan_at && sdata.last_scan_at !== baselineScanAt) {
                                                    stopPolling();
                                                    setRefreshTick(t => t + 1);
                                                    setScanBanner({
                                                        tone: 'green',
                                                        text: 'Scan complete. Mentions refreshed.',
                                                    });
                                                }
                                            } catch (_) { /* ignore, keep polling */ }
                                        };
                                        await pollOnce();
                                        if (scanPollRef.current === null) {
                                            scanPollRef.current = setInterval(pollOnce, 2 * 60 * 1000);
                                            scanPollTimeoutRef.current = setTimeout(stopPolling, 30 * 60 * 1000);
                                        }
                                    }
                                }
                            } catch (err) {
                                const data = err?.response?.data || {};
                                console.log("pp", data);

                                if (data.error === 'rate_limited') {
                                    setScanBanner({
                                        tone: 'amber',
                                        text: formatRateLimitedMessage(data?.message, scanStatus?.cadence_minutes),
                                    });
                                } else {
                                    setScanBanner({
                                        tone: 'red',
                                        text: `Could not start scan: ${data.error || err?.message || 'failed'}`,
                                    });
                                }
                            } finally {
                                setScanBusy(false);
                            }
                        }}
                        title="Trigger an immediate brand-mention scan against your current watchlist. Use after adding anchors in Settings."
                        style={{
                            padding: '6px 14px', borderRadius: 6,
                            border: '1px solid #2563eb',
                            background: scanBusy ? '#93c5fd' : '#2563eb',
                            color: '#ffffff', fontWeight: 600, fontSize: 12,
                            cursor: scanBusy ? 'wait' : 'pointer',
                            whiteSpace: 'nowrap',
                        }}>
                        {scanBusy ? '⏳ Scanning…' : '⚡ Scan now'}
                    </button>
                </div>
            </div>
            {scanBanner && (() => {
                // 2026-05-26 contrast pass — page is light-theme, so the
                // dark-theme fg colors (#fecaca etc.) were unreadable.
                // Switched to 700-tier saturated text on tinted background.
                const tone = scanBanner.tone;
                const palette = tone === 'red'
                    ? { bg: 'rgba(220,38,38,0.10)', fg: '#b91c1c', border: 'rgba(220,38,38,0.45)' }
                    : tone === 'amber'
                        ? { bg: 'rgba(245,158,11,0.15)', fg: '#b45309', border: 'rgba(245,158,11,0.55)' }
                        : { bg: 'rgba(34,197,94,0.12)', fg: '#15803d', border: 'rgba(34,197,94,0.45)' };
                return (
                    <div style={{
                        margin: '6px 0 4px', padding: '6px 12px', borderRadius: 4,
                        background: palette.bg, color: palette.fg,
                        border: `1px solid ${palette.border}`,
                        fontSize: 12, fontWeight: 500, textAlign: 'right',
                    }}>
                        {scanBanner.text}
                    </div>
                );
            })()}
            {(newSinceCount > 0 || (scanStatus && scanStatus.last_scan_at)
                || (alertLatency && alertLatency.measured > 0)) && (
                    <div style={{
                        fontSize: 11, color: '#475569',
                        padding: '2px 0 0', textAlign: 'right',
                        display: 'flex', justifyContent: 'flex-end',
                        gap: 12, alignItems: 'center'
                    }}>
                        {newSinceCount > 0 && (
                            <button type="button"
                                onClick={() => {
                                    try {
                                        const orgId = localStorage.getItem('organization_id') || '0';
                                        localStorage.setItem(`bm_last_viewed_at_org_${orgId}`, new Date().toISOString());
                                    } catch { }
                                    setNewSinceCount(0);
                                    setRefreshTick(t => t + 1);
                                }}
                                title="Mark as viewed and refresh the feed"
                                style={{
                                    border: '1px solid rgba(59,130,246,0.55)',
                                    background: 'rgba(59,130,246,0.15)',
                                    color: '#1d4ed8',
                                    borderRadius: 999, padding: '2px 10px',
                                    fontSize: 11, fontWeight: 600,
                                    cursor: 'pointer',
                                }}>
                                {newSinceCount} new since you last viewed →
                            </button>
                        )}
                        {scanStatus && scanStatus.last_scan_at && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#475569' }}>
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '2px 9px', borderRadius: 999,
                                    background: scanStatus.manual_only
                                        ? 'rgba(148,163,184,0.15)'
                                        : 'rgba(16,185,129,0.12)',
                                    color: scanStatus.manual_only ? '#475569' : '#047857',
                                    border: `1px solid ${scanStatus.manual_only
                                        ? 'rgba(148,163,184,0.40)'
                                        : 'rgba(16,185,129,0.45)'}`,
                                    fontSize: 11, fontWeight: 600, letterSpacing: 0.2,
                                }}>
                                    <span style={{
                                        width: 6, height: 6, borderRadius: '50%',
                                        background: scanStatus.manual_only ? '#94a3b8' : '#10b981',
                                        boxShadow: scanStatus.manual_only
                                            ? 'none'
                                            : '0 0 0 3px rgba(16,185,129,0.18)',
                                    }} />
                                    {scanStatus.manual_only ? 'Manual only' : 'Monitoring active'}
                                </span>
                                <span>Last update: {timeAgo(scanStatus.last_scan_at)}</span>
                            </span>
                        )}
                        {/* <AlertLatencyPill data={alertLatency} /> */}
                    </div>
                )}
            {!filtersAreCustom && (
                <div style={{
                    fontSize: 11, color: '#94a3b8', padding: '4px 0 8px',
                    textAlign: 'right'
                }}>
                    Tip — narrow this feed using the chips above, then save the filter
                    set so you can re-apply it across sessions.
                </div>
            )}

            <div className="bm_grid">
                <div className="bm_main">
                    <div className="bm_chart_card">
                        <div className="bm_chart_tabs">
                            <button className={chartMode === 'mentions_reach' ? 'active' : ''}
                                onClick={() => setChartMode('mentions_reach')}>Mentions & Reach</button>
                            <button className={chartMode === 'sentiment' ? 'active' : ''}
                                onClick={() => setChartMode('sentiment')}>Sentiment</button>
                            <span className="bm_chart_bucket">
                                {['days', 'weeks', 'months'].map(b => (
                                    <button key={b} className={bucket === b ? 'active' : ''}
                                        onClick={() => setBucket(b)}>{b}</button>
                                ))}
                            </span>
                        </div>
                        <MentionsReachChart
                            series={chartMode === 'sentiment'
                                ? chart.sentiment_series
                                : chart.mentions_reach_series}
                            mode={chartMode}
                            onSpikeClick={spike => setSelectedSpike(spike)} />
                        {selectedSpike && (
                            <SpikePanel spike={selectedSpike}
                                onClose={() => setSelectedSpike(null)}
                                onDrillIn={s => {
                                    setBucketFilter({ from: s.bucket, to: s.bucket });
                                    setPage(1);
                                }}
                                onShowAll={() => {
                                    setBucketFilter({ from: '', to: '' });
                                    setPage(1);
                                }} />
                        )}
                        {(bucketFilter.from || bucketFilter.to) && (
                            <div className="bm_bucket_filter_banner">
                                Feed filtered to{' '}
                                <strong>{bucketFilter.from === bucketFilter.to
                                    ? bucketFilter.from
                                    : `${bucketFilter.from || '…'} → ${bucketFilter.to || '…'}`}</strong>.
                                <button onClick={() => { setBucketFilter({ from: '', to: '' }); setPage(1); }}
                                    className="bm_bucket_filter_clear">
                                    Clear date filter ×
                                </button>
                            </div>
                        )}
                        {(monitoredTopic || narrativeFilter) && (
                            <div className="bm_bucket_filter_banner">
                                Feed filtered to topic{' '}
                                <strong>{monitoredTopic || narrativeFilter}</strong>.
                                <button onClick={() => {
                                    setMonitoredTopic('');
                                    setNarrativeFilter('');
                                    setPage(1);
                                }}
                                    className="bm_bucket_filter_clear">
                                    Clear topic filter ×
                                </button>
                            </div>
                        )}
                        {chartMode === 'mentions_reach' && (
                            <div className="bm_chart_legend">
                                <span><span className="dot" style={{ background: '#3b82f6' }} /> Mentions</span>
                                <span><span className="dot" style={{ background: '#16a34a' }} /> Reach</span>
                            </div>
                        )}
                        {chartMode === 'sentiment' && (
                            <div className="bm_chart_legend">
                                <span><span className="dot" style={{ background: SENTIMENT_COLOR.positive }} /> Positive</span>
                                <span><span className="dot" style={{ background: SENTIMENT_COLOR.neutral }} /> Neutral</span>
                                <span><span className="dot" style={{ background: SENTIMENT_COLOR.negative }} /> Negative</span>
                            </div>
                        )}
                    </div>

                    <Pagination position="top"
                        page={page} pages={feed.pages || 0}
                        total={feed.total || 0} pageSize={pageSize}
                        onPage={p => {
                            setPage(p);
                            // Scroll to the top of the feed so the
                            // analyst lands on the new page rather
                            // than mid-list.
                            if (typeof window !== 'undefined') {
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                        }}
                        onPageSize={n => { setPageSize(n); setPage(1); }} />

                    <div className="bm_feed">
                        {(feed.results || []).map(m => (
                            <MentionCard key={m.id} m={m}
                                orgTags={orgTags}
                                onVisitChange={handleVisitChange}
                                onMute={handleMuteHost}
                                onTagsChange={handleTagsChange}
                                onOrgTagsRefresh={refreshOrgTags}
                                onQueueChange={handleQueueChange}
                                onTakedown={handleTakedownSubmitted}
                                onVerdictChange={handleVerdictChange} />
                        ))}
                        {/* 2026-05-27 UX cleanup — skeleton placeholders
                            during load instead of a blank gap. Three
                            ghost-cards match the typical first-paint
                            sized of the feed. */}
                        {feedLoading && (feed.results || []).length === 0 && (
                            <>
                                {[0, 1, 2].map(i => (
                                    <div key={i} style={{
                                        background: '#ffffff',
                                        border: '1px solid rgba(148,163,184,0.25)',
                                        borderRadius: 8,
                                        padding: 14, marginBottom: 8,
                                        display: 'flex', gap: 12,
                                    }}>
                                        <div style={{
                                            width: 44, height: 44, borderRadius: '50%',
                                            background: 'rgba(148,163,184,0.2)'
                                        }} />
                                        <div style={{ flex: 1 }}>
                                            <Skeleton width="35%" height={12} />
                                            <div style={{ marginTop: 6 }}>
                                                <Skeleton width="90%" height={10} />
                                            </div>
                                            <div style={{ marginTop: 4 }}>
                                                <Skeleton width="70%" height={10} />
                                            </div>
                                            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                                                <Skeleton width={80} height={20} />
                                                <Skeleton width={80} height={20} />
                                                <Skeleton width={120} height={20} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                        {!feedLoading && (feed.results || []).length === 0 && (
                            <EmptyState
                                mark="line"
                                title="No mentions match these filters"
                                body={(() => {
                                    if (filtersAreCustom) {
                                        const active = [];
                                        if (platforms.length) active.push(`platforms: ${platforms.join(', ')}`);
                                        if (sentiments.length) active.push(`sentiment: ${sentiments.join(', ')}`);
                                        if (riskOnly) active.push('risk-tagged only');
                                        if (visitedMode !== 'all') active.push(`${visitedMode} only`);
                                        if (days !== 30) active.push(`last ${days}d`);
                                        return `${active.length ? `Active filters → ${active.join(' · ')}. ` : ''}Try clearing a filter, widening the date range, or hitting Refresh.`;
                                    }
                                    return 'No mentions captured in this window yet. Either the scanner hasn\'t run yet, or your watchlist anchors are too narrow. Add anchors in Settings → Mention anchors, then click Scan now.';
                                })()}
                                action={filtersAreCustom ? (
                                    <button className="btn btn-sm btn-secondary" onClick={clearFilters}>Clear all filters</button>
                                ) : (
                                    <button className="btn btn-sm btn-primary"
                                        onClick={() => window.dispatchEvent(
                                            new CustomEvent('bm-tab-switch', { detail: { tab: 'settings' } }))}>
                                        Open Settings → Mention anchors
                                    </button>
                                )}
                            />
                        )}
                    </div>

                    <Pagination position="bottom"
                        page={page} pages={feed.pages || 0}
                        total={feed.total || 0} pageSize={pageSize}
                        onPage={p => {
                            setPage(p);
                            if (typeof window !== 'undefined') {
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                        }}
                        onPageSize={n => { setPageSize(n); setPage(1); }} />
                </div>

                <div className="bm_rail">
                    {/* Brand24-parity slice 3 — Filters card (Importance / Visited
                        segmented controls + Influence slider). Always at the top
                        so an analyst's hand-eye lands on it first. */}
                    <div className="bm_rail_card bm_rail_filters">
                        <h4>Filters</h4>

                        <div className="bm_rail_filter_block">
                            <label className="bm_rail_filter_label">Importance</label>
                            <div className="bm_visited_seg" role="radiogroup">
                                {[
                                    { key: 'all', label: 'All' },
                                    { key: 'important', label: 'Important' },
                                ].map(opt => (
                                    <button key={opt.key} role="radio"
                                        aria-checked={importanceMode === opt.key}
                                        className={`bm_visited_seg_btn ${importanceMode === opt.key ? 'active' : ''}`}
                                        onClick={() => { setImportanceMode(opt.key); setPage(1); }}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <div className="bm_rail_filter_hint">
                                Important = risk-tagged sources or reach ≥ 75th percentile.
                            </div>
                        </div>

                        <div className="bm_rail_filter_block">
                            <label className="bm_rail_filter_label">Visited</label>
                            <div className="bm_visited_seg" role="radiogroup">
                                {[
                                    { key: 'all', label: 'All' },
                                    { key: 'unread', label: 'Unread' },
                                    { key: 'read', label: 'Read' },
                                ].map(opt => (
                                    <button key={opt.key} role="radio"
                                        aria-checked={visitedMode === opt.key}
                                        className={`bm_visited_seg_btn ${visitedMode === opt.key ? 'active' : ''}`}
                                        onClick={() => { setVisitedMode(opt.key); setPage(1); }}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="bm_rail_filter_block">
                            <label className="bm_rail_filter_label">Influence score (reach)</label>
                            <div className="bm_rail_range">
                                <input type="number" placeholder="min"
                                    value={influenceMin}
                                    min={0}
                                    onChange={e => { setInfluenceMin(e.target.value); setPage(1); }} />
                                <span>–</span>
                                <input type="number" placeholder="max"
                                    value={influenceMax}
                                    min={0}
                                    onChange={e => { setInfluenceMax(e.target.value); setPage(1); }} />
                                {(influenceMin !== '' || influenceMax !== '') && (
                                    <button className="bm_rail_range_clear"
                                        onClick={() => { setInfluenceMin(''); setInfluenceMax(''); setPage(1); }}
                                        title="Clear influence range">
                                        ×
                                    </button>
                                )}
                            </div>
                            <div className="bm_rail_filter_hint">
                                Filter by reach_score. Posts on x.com with 10k+ reach typically land at 50+.
                            </div>
                        </div>

                        {/* Slice 3-B — Language filter (multi-select chips). */}
                        {(filterOptions.languages || []).length > 0 && (
                            <div className="bm_rail_filter_block">
                                <label className="bm_rail_filter_label">Language</label>
                                <div className="bm_rail_chiprow">
                                    {(filterOptions.languages || []).map(l => {
                                        const on = activeLanguages.includes(l);
                                        return (
                                            <button key={l}
                                                className={`bm_rail_chip ${on ? 'active' : ''}`}
                                                onClick={() => {
                                                    setActiveLanguages(prev => prev.includes(l)
                                                        ? prev.filter(x => x !== l)
                                                        : [...prev, l]);
                                                    setPage(1);
                                                }}>
                                                {l}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* emotions section  */}
                        {(filterOptions.emotions || []).length > 0 && (
                            <div className="bm_rail_filter_block">
                                <label className="bm_rail_filter_label">Emotion</label>
                                <div className="bm_rail_chiprow">
                                    {(filterOptions.emotions || []).map(l => {
                                        const on = activeEmotions.includes(l);
                                        return (
                                            <button key={l}
                                                className={`bm_rail_chip ${on ? 'active' : ''}`}
                                                onClick={() => {
                                                    setActiveEmotions(prev => prev.includes(l)
                                                        ? prev.filter(x => x !== l)
                                                        : [...prev, l]);
                                                    setPage(1);
                                                }}>
                                                {l}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* emotions section  */}
                        {(filterOptions.narratives || []).length > 0 && (
                            <div className="bm_rail_filter_block">
                                <label className="bm_rail_filter_label">Narrative</label>
                                <div className="bm_rail_chiprow">
                                    {(filterOptions.narratives || []).map(l => {
                                        const on = activeNarratives.includes(l);
                                        return (
                                            <button key={l}
                                                className={`bm_rail_chip ${on ? 'active' : ''}`}
                                                onClick={() => {
                                                    setActiveNarratives(prev => prev.includes(l)
                                                        ? prev.filter(x => x !== l)
                                                        : [...prev, l]);
                                                    setPage(1);
                                                }}>
                                                {l}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Slice 3-B — Emotion filter (multi-select chips). */}
                        {/* {(filterOptions.emotions || []).length > 0 && (
                            <div className="bm_rail_filter_block">
                                <label className="bm_rail_filter_label">Emotion</label>
                                <div className="bm_rail_chiprow">
                                    {(filterOptions.emotions || []).map(e => {
                                        const on = activeEmotions.includes(e);
                                        return (
                                            <button key={e}
                                                    className={`bm_rail_chip bm_rail_chip--emo-${e} ${on ? 'active' : ''}`}
                                                    onClick={() => {
                                                        setActiveEmotions(prev => prev.includes(e)
                                                            ? prev.filter(x => x !== e)
                                                            : [...prev, e]);
                                                        setPage(1);
                                                    }}>
                                                {e}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )} */}
                    </div>

                    {/* Sources — clickable platform rows. Click toggles the
                        platform filter. Slice-3 parity with Brand24's
                        always-visible sources rail. */}
                    <div className="bm_rail_card">
                        <h4>Sources</h4>
                        {sources.map(s => {
                            const on = platforms.includes((s.platform || '').toLowerCase());
                            return (
                                <button key={s.platform}
                                    className={`bm_rail_row bm_rail_row--clickable ${on ? 'active' : ''}`}
                                    onClick={() => toggleInList(s.platform, platforms, setPlatforms)}
                                    title={on ? `Click to unfilter ${s.platform}` : `Click to filter to ${s.platform}`}>
                                    <span className="bm_rail_label">{s.platform}</span>
                                    <span className="bm_rail_value">{s.mentions}</span>
                                </button>
                            );
                        })}
                        {sources.length === 0 && <div className="bm_rail_empty">No data</div>}
                    </div>

                    {/* Sentiment rail — click to toggle filter (parity
                        with Sources rail). Replaces the top-toolbar
                        Sentiment chips after the 2026-05-27 cleanup. */}
                    <div className="bm_rail_card">
                        <h4>Sentiment</h4>
                        {Object.entries(summary.by_sentiment || {}).map(([k, v]) => {
                            const on = sentiments.map(s => s.toLowerCase()).includes(k.toLowerCase());
                            return (
                                <button key={k}
                                    className={`bm_rail_row bm_rail_row--clickable ${on ? 'active' : ''}`}
                                    onClick={() => toggleInList(k, sentiments, setSentiments)}
                                    title={on ? `Click to unfilter ${k}` : `Click to filter to ${k}`}>
                                    <span className="bm_rail_dot" style={{ background: SENTIMENT_COLOR[k] || '#94a3b8' }} />
                                    <span className="bm_rail_label">{k}</span>
                                    <span className="bm_rail_value">{v}</span>
                                </button>
                            );
                        })}
                        {Object.keys(summary.by_sentiment || {}).length === 0 && (
                            <div className="bm_rail_empty">No data</div>
                        )}
                    </div>
                    <div className="bm_rail_card">
                        <h4>Top Influencers</h4>
                        {influencers.slice(0, 10).map(i => (
                            <div key={i.handle + i.platform} className="bm_inf_row">
                                <span className="bm_inf_handle">{i.handle}</span>
                                <span className="bm_inf_platform">{i.platform}</span>
                                <span className="bm_inf_score">{i.avg_influence_score}/10</span>
                                {i.risk_tagged && <span className="bm_inf_risk">⚠</span>}
                            </div>
                        ))}
                        {influencers.length === 0 && <div className="bm_rail_empty">No data</div>}
                    </div>
                </div>
            </div>
        </>
    );
}
